import { CardComponent } from "@chargebee/chargebee-js-react-wrapper";
import { ArrowForward } from "@mui/icons-material";
import {
  BaseTextFieldProps,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  InputAdornment,
  MenuItem,
  Modal,
  Select,
  TextField,
  Typography
} from "@mui/material";
import { CheckboxProps } from "@mui/material/Checkbox";
import { styled } from "@mui/material/styles";
import moment from "moment-timezone";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  CountryIso2,
  defaultCountries,
  FlagImage,
  parseCountry,
  usePhoneInput,
} from "react-international-phone";
import "react-international-phone/style.css";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  LogoDarkMode
} from "../../assets/images";
import SignupFormSkeleton from "../../components/skeltons/SignupFormSkeleton";
import {
  AUTOMATED_PRICES,
  LIVE_AUTO_PRICES,
  LIVE_PRICES,
  PricingTier,
} from "../../components/subscription/pricing/pricingData";
import { config } from "../../config";
import { PLAN_NAMES } from "../../constants/planNames";
import AccountVerification from "../../modals/AccountVerification";
import SalesExperienceYearlyOffer from "../../modals/salesExperience/SalesExperienceYearlyOffer";
import AuthService from "../../services/auth.service";
import chargebeeService from "../../services/chargebee.service";
import EventsLoggerService from "../../services/events-logger.service";
import { LeadsquaredService } from "../../services/leadsquared/leadsquared.service";
import { GhlService } from "../../services/ghl";
import theme from "../../theme";
import { SocialProvider } from "../../types/auth";
import { Applications } from "../../types/enums";
import { generateSecurePassword, validateEmail } from "../../utils/common.util";
import {
  EVENT_TYPES,
  logEvent,
  SIGNUP_FLOWS,
  SIGNUP_STEPS,
} from "../../utils/events.util";

// Type definition for plan IDs
type PlanId = "All-In-One-USD-Yearly" | "All-In-One-USD-Monthly" | "free-trial-plan" | "Live-Webinars-USD-Yearly" | "Live-Webinars-USD-Monthly";

// Add CheckCircleIcon component
const CheckCircleIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="28"
    height="29"
    viewBox="0 0 28 29"
    fill="none"
  >
    <path
      d="M24.5459 11.6742C24.1194 11.2291 23.6786 10.7697 23.5014 10.342C23.3373 9.945 23.3286 9.35766 23.3188 8.73641C23.3034 7.71047 23.2859 6.54891 22.5072 5.77016C21.7284 4.99141 20.5625 4.97391 19.5409 4.95859C18.9197 4.94875 18.3378 4.94 17.9353 4.77594C17.5077 4.59875 17.0483 4.15797 16.6031 3.73141C15.8769 3.03578 15.0544 2.24609 14 2.24609C12.9456 2.24609 12.1231 3.03578 11.3969 3.73141C10.9517 4.15797 10.4923 4.59875 10.0647 4.77594C9.66766 4.94 9.08031 4.94875 8.45906 4.95859C7.43312 4.97391 6.27156 4.99141 5.49281 5.77016C4.71406 6.54891 4.69656 7.71484 4.68125 8.73641C4.67141 9.35766 4.66266 9.93953 4.49859 10.342C4.32141 10.7697 3.88063 11.2291 3.45406 11.6742C2.75844 12.4005 1.96875 13.223 1.96875 14.2773C1.96875 15.3317 2.75844 16.1542 3.45406 16.8805C3.88063 17.3256 4.32141 17.785 4.49859 18.2127C4.66266 18.6097 4.67141 19.197 4.68125 19.8183C4.69656 20.8442 4.71406 22.0058 5.49281 22.7845C6.27156 23.5633 7.4375 23.5808 8.45906 23.5961C9.08031 23.6059 9.66219 23.6147 10.0647 23.7787C10.4923 23.9559 10.9517 24.3967 11.3969 24.8233C12.1231 25.5189 12.9456 26.3086 14 26.3086C15.0544 26.3086 15.8769 25.5189 16.6031 24.8233C17.0483 24.3967 17.5077 23.9559 17.9353 23.7787C18.3323 23.6147 18.9197 23.6059 19.5409 23.5961C20.5669 23.5808 21.7284 23.5633 22.5072 22.7845C23.2859 22.0058 23.3034 20.8442 23.3188 19.8183C23.3286 19.197 23.3373 18.6152 23.5014 18.2127C23.6786 17.785 24.1194 17.3256 24.5459 16.8805C25.2416 16.1542 26.0312 15.3317 26.0312 14.2773C26.0312 13.223 25.2416 12.4005 24.5459 11.6742ZM23.5987 15.9727C23.088 16.5042 22.5597 17.0555 22.2862 17.7117C22.0259 18.3395 22.015 19.0811 22.0041 19.7986C21.992 20.6123 21.9789 21.4545 21.5775 21.857C21.1761 22.2595 20.3328 22.2716 19.5191 22.2836C18.8016 22.2945 18.06 22.3055 17.4322 22.5658C16.7759 22.837 16.2291 23.3653 15.692 23.8783C15.1178 24.4252 14.5239 24.9983 13.9978 24.9983C13.4717 24.9983 12.8778 24.4284 12.3025 23.8783C11.7709 23.3675 11.2197 22.8392 10.5634 22.5658C9.93563 22.3055 9.19406 22.2945 8.47656 22.2836C7.66281 22.2716 6.82062 22.2584 6.41813 21.857C6.01562 21.4556 6.00359 20.6123 5.99156 19.7986C5.98062 19.0811 5.96969 18.3395 5.70938 17.7117C5.43813 17.0555 4.90984 16.5086 4.39688 15.9716C3.85109 15.3973 3.28125 14.8034 3.28125 14.2773C3.28125 13.7513 3.85109 13.1573 4.40125 12.582C4.91203 12.0505 5.44031 11.4992 5.71375 10.843C5.97406 10.2152 5.985 9.47359 5.99594 8.75609C6.00797 7.94234 6.02109 7.10016 6.4225 6.69766C6.82391 6.29516 7.66719 6.28312 8.48094 6.27109C9.19844 6.26016 9.94 6.24922 10.5678 5.98891C11.2241 5.71766 11.7709 5.18938 12.308 4.67641C12.88 4.12844 13.4739 3.55859 14 3.55859C14.5261 3.55859 15.12 4.12844 15.6953 4.67859C16.2269 5.18938 16.7781 5.71766 17.4344 5.99109C18.0622 6.25141 18.8037 6.26234 19.5212 6.27328C20.335 6.28531 21.1772 6.29844 21.5797 6.69984C21.9822 7.10125 21.9942 7.94453 22.0063 8.75828C22.0172 9.47578 22.0281 10.2173 22.2884 10.8452C22.5597 11.5014 23.088 12.0483 23.6009 12.5853C24.1478 13.1595 24.7209 13.7534 24.7209 14.2795C24.7209 14.8056 24.1489 15.3973 23.5987 15.9727ZM18.8388 11.1886C18.9616 11.3116 19.0307 11.4784 19.0307 11.6523C19.0307 11.8263 18.9616 11.993 18.8388 12.1161L12.7137 18.2411C12.5907 18.364 12.4239 18.433 12.25 18.433C12.0761 18.433 11.9093 18.364 11.7863 18.2411L9.16125 15.6161C9.04533 15.4917 8.98222 15.3272 8.98522 15.1571C8.98822 14.9871 9.0571 14.8249 9.17733 14.7047C9.29757 14.5844 9.45978 14.5156 9.62979 14.5126C9.79981 14.5096 9.96435 14.5727 10.0887 14.6886L12.25 16.8487L17.9112 11.1886C18.0343 11.0657 18.2011 10.9967 18.375 10.9967C18.5489 10.9967 18.7157 11.0657 18.8388 11.1886Z"
      fill="black"
    />
  </svg>
);

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

export interface MUIPhoneProps extends BaseTextFieldProps {
  value: string;
  onChange: (phone: string) => void;
}

export const MuiPhone: React.FC<MUIPhoneProps> = ({
  value,
  onChange,
  ...restProps
}) => {
  const { inputValue, handlePhoneValueChange, inputRef, country, setCountry } =
    usePhoneInput({
      defaultCountry: "us",
      value,
      countries: defaultCountries,
      onChange: (data) => {
        onChange(data.phone);
      },
      disableCountryGuess: true,
      forceDialCode: true,
    });

  return (
    <TextField
      variant="outlined"
      label="Phone number"
      color="primary"
      placeholder="Phone number"
      value={inputValue}
      onChange={handlePhoneValueChange}
      type="tel"
      inputRef={inputRef}
      sx={{
        "& .MuiOutlinedInput-root": {
          borderRadius: "3px",
          borderColor: "#666666",
        },

        " .MuiInputLabel-root.Mui-focused": {
          color: "inherit",
        },

        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
        {
          borderColor: "#333333",
          border: "1px solid",
        },

        "& .MuiOutlinedInput-input": {
          padding: "14px 16px",
          background: "#ffffff",
          borderRadius: "8px",
        },
        "&.MuiInputBase-input::placeholder": {
          color: "#333333",
          fontSize: "16px",
          opacity: "0.8",
        },
      }}
      slotProps={{
        input: {
          startAdornment: (
            <InputAdornment
              position="start"
              style={{ marginRight: "2px", marginLeft: "-8px" }}
            >
              <Select
                MenuProps={{
                  style: {
                    height: "300px",
                    width: "360px",
                    top: "10px",
                    left: "-34px",
                  },
                  transformOrigin: {
                    vertical: "top",
                    horizontal: "left",
                  },
                }}
                sx={{
                  width: "max-content",
                  fieldset: {
                    display: "none",
                  },
                  '&.Mui-focused:has(div[aria-expanded="false"])': {
                    fieldset: {
                      display: "block",
                    },
                  },
                  ".MuiSelect-select": {
                    padding: "8px",
                    paddingRight: "24px !important",
                  },
                  svg: {
                    right: 0,
                  },
                }}
                value={country.iso2}
                onChange={(e) => setCountry(e.target.value as CountryIso2)}
                renderValue={(value) => (
                  <FlagImage iso2={value} style={{ display: "flex" }} />
                )}
              >
                {defaultCountries.map((c) => {
                  const country = parseCountry(c);
                  return (
                    <MenuItem key={country.iso2} value={country.iso2}>
                      <FlagImage
                        iso2={country.iso2}
                        style={{ marginRight: "8px" }}
                      />
                      <Typography marginRight="8px">{country.name}</Typography>
                      <Typography color="gray">+{country.dialCode}</Typography>
                    </MenuItem>
                  );
                })}
              </Select>
            </InputAdornment>
          ),
        },
      }}
      {...restProps}
    />
  );
};

const SignupSales14DayTrialWithBook = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const allowedPlanIds = ["All-In-One-USD-Monthly", "All-In-One-USD-Yearly", "Live-Webinars-USD-Monthly", "Live-Webinars-USD-Yearly"];
  const bookPrice = 7;
  const [searchParams, setSearchParams] = useSearchParams();
  const emailFromUrl = searchParams.get("email");
  const flowParam = searchParams.get("flow");
  const isEbookFlow = flowParam === "e-book";
  const planId = searchParams.get("planId");
  const cleanPlanId = planId ? planId.replace(/-\d+$/, "") : undefined;
  const isProfessionalPlan = cleanPlanId?.includes("All-In-One");

  const [showPassword, setShowPassword] = React.useState(false);
  const [showPromoField, setShowPromoField] = useState(false);
  const [value, setValue] = React.useState(0);
  const [accountExists, setAccountExists] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [includeEbook, setIncludeEbook] = useState(false);
  const [totalDueToday, setTotalDueToday] = useState(1);
  const [termsError, setTermsError] = useState("");
  const [leadSquaredId, setLeadSquaredId] = useState<string>("");
  const [ghlContactId, setGhlContactId] = useState<string>("");
  const [selectedPlanId, setSelectedPlanId] = useState<PlanId>("All-In-One-USD-Monthly");
  const [cards] = useState([
    {
      name: "Amy Porterfield",
      title: "Online Marketing Expert",
      testimonial: `"I personally use EasyWebinar and they are the best. I moved from using other platforms and nothing compares to their reliability and customer support!"`,
      avatar:
        "https://www.amyporterfield.com/wp-content/uploads/2022/12/Amy-Porterfield-Headshot-Course-Creator-2-2-scaled.jpg",
    },
  ]);

  const getTimezoneString = () => {
    const timezone = moment.tz.guess() || "UTC";
    const offset = moment.tz(timezone).format("Z");
    const zoneName = moment.tz(timezone).zoneName();
    return `(GMT${offset}),${timezone},${zoneName}`;
  };

  const generateFirstName = (email: string) => {
    const emailParts = email.split("@")[0];
    const name = emailParts
      .split(/[._-]/)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(" ");
    return name;
  };

  const [formData, setFormData] = useState({
    email: "",
    password: "",
    timezone: getTimezoneString(),
    subdomain: "",
  });

  // Auto-generate strong password on component mount
  useEffect(() => {
    const autoGeneratedPassword = generateSecurePassword();
    setFormData((prev) => ({
      ...prev,
      password: autoGeneratedPassword,
    }));
  }, []);
  const [formErrors, setFormErrors] = useState({
    email: "",
    password: "",
    subdomain: "",
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingUpgrade, setIsLoadingUpgrade] = useState({
    accept: false,
    decline: false,
  });
  const [isAutoRegistering, setIsAutoRegistering] = useState(false);
  const [isCardReady, setIsCardReady] = useState(false);
  const [isCardValid, setIsCardValid] = useState(false);

  const [verificationData, setVerificationData] = useState({
    companyId: "",
    companyName: "",
    infLeadSquaredId: "",
  });

  const [cardToken, setCardToken] = useState<string>("");
  const [cardError, setCardError] = useState<string | null>(null);
  const cardRef = useRef<any>(null);
  const hasProcessedEmailFromUrl = useRef<boolean>(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);



  const [planDetails, setPlanDetails] = useState<any>(null);
  const [isLoadingPlan, setIsLoadingPlan] = useState(false);
  const [isTrial, setIsTrial] = useState(false);
  const [noTrial, setNoTrial] = useState(false);
  const [monthlyPlanDetails, setMonthlyPlanDetails] = useState<any>(null);
  const [initialPlanId, setInitialPlanId] = useState<string | null>(null);

  const [promoCode, setPromoCode] = useState("");
  const [appliedPromo, setAppliedPromo] = useState<{
    code: string;
    discount: number;
    type: string;
  } | null>(null);
  const [promoError, setPromoError] = useState("");

  const [domainSuggestions, setDomainSuggestions] = useState<string[]>([]);

  // Update total due today based on ebook checkbox
  useEffect(() => {
    if (isEbookFlow) {
      setTotalDueToday(includeEbook ? 1 + bookPrice : 1);
    }
  }, [includeEbook, isEbookFlow, bookPrice]);



  // Initialize billing period from URL
  const initializeBillingPeriodFromUrl = () => {
    if (cleanPlanId) {
      return cleanPlanId.includes("Yearly");
    }
    return false; // Default to monthly
  };

  const [isYearly, setIsYearly] = useState(initializeBillingPeriodFromUrl());

  // Update plan in URL when billing period changes
  const updatePlanInUrl = (newIsYearly: boolean) => {
    if (!planId) return;

    // Extract the attendee number from the current planId
    const attendeeMatch = planId.match(/-(\d+)$/);
    const attendeeNumber = attendeeMatch ? attendeeMatch[1] : "250"; // Default to 250 if no number found

    // Extract the base plan ID without period and attendee number
    const basePlanId = planId
      .replace(/(Monthly|Yearly)/g, "")
      .replace(/-\d+$/, "");
    const period = newIsYearly ? "Yearly" : "Monthly";
    const newPlanId = `${basePlanId}${period}-${attendeeNumber}`;

    setSearchParams(
      (prev) => {
        prev.set("planId", newPlanId);
        return prev;
      },
      { replace: true }
    );
  };

  // Set default planId if not present and track initial plan ID
  useEffect(() => {
    if (!planId) {
      // Use setSearchParams to update only the planId without reloading the page
      setSearchParams(
        (prev) => {
          prev.set("planId", "All-In-One-USD-Monthly-200");
          return prev;
        },
        { replace: true }
      );
    } else if (!initialPlanId) {
      // Set the initial plan ID only once
      setInitialPlanId(planId);
    }

    // Check if this is a free trial plan
    setIsTrial(planId === PLAN_NAMES.FREE_TRIAL);
  }, [planId, setSearchParams, initialPlanId]);

  // Store planId in localStorage when component mounts
  useEffect(() => {
    if (planId) {
      localStorage.setItem("planId", planId);
      // const autoApplyPromo = async () => {
      //     if (!appliedPromo && planId) {
      //         await validateCoupon();
      //     }
      // };
      // autoApplyPromo();
    }
    
  }, [planId]);

  // No longer needed as signup is handled on offer page
  // useEffect(() => {
  //     const locationState = location.state as any;
  //     if (locationState?.proceedWithSignup && locationState?.selectedPlan) {
  //         // Restore form data
  //         if (locationState.formData) {
  //             setFormData(locationState.formData);
  //         }
  //         if (locationState.promoCode) {
  //             setPromoCode(locationState.promoCode);
  //         }
  //         if (locationState.leadSquaredId) {
  //             setLeadSquaredId(locationState.leadSquaredId);
  //         }
  //
  //         // Proceed with signup using selected plan
  //         handleProceedWithSignup(locationState.selectedPlan);
  //
  //         // Clear the state to prevent reprocessing
  //         navigate(location.pathname, { replace: true, state: {} });
  //     }
  // }, [location.state]);

  useEffect(() => {
    const fetchPlanDetails = async () => {
      if (!initialPlanId) return;

      const cleanInitialPlanId = initialPlanId.replace(/-\d+$/, "");
      setIsLoadingPlan(true);
      try {
        // Fetch both monthly and yearly plan details at once using clean plan IDs without numbers
        const basePlanId = cleanInitialPlanId.replace(/(Monthly|Yearly)/g, "");
        const monthlyPlanId = `${basePlanId}Monthly`;
        const yearlyPlanId = `${basePlanId}Yearly`;

        // Fetch both plans in parallel
        const [monthlyResponse, yearlyResponse] = await Promise.all([
          chargebeeService.getPlanById(monthlyPlanId),
          chargebeeService.getPlanById(yearlyPlanId),
        ]);

        // Store both plan details
        setPlanDetails(isYearly ? yearlyResponse.data : monthlyResponse.data);
        setMonthlyPlanDetails(
          isYearly ? monthlyResponse.data : yearlyResponse.data
        );
      } catch (error) {
        console.error("Failed to fetch plan details:", error);
        // Fallback: try to fetch just the current plan
        try {
          const response = await chargebeeService.getPlanById(
            cleanInitialPlanId
          );
          setPlanDetails(response.data);
        } catch (fallbackError) {
          console.error(
            "Failed to fetch fallback plan details:",
            fallbackError
          );
        }
      } finally {
        setIsLoadingPlan(false);
      }
    };

    fetchPlanDetails();
  }, [initialPlanId]); // Only depend on initialPlanId, not planId

  useEffect(() => {
    // Extract plan details from planId and console log them
    if (planId) {
      // Parse the planId
      const parts = planId.split("-");
      let extractedPlanType: string;
      let currency: string;
      let duration: string;
      let extractedAttendees: number;
      let liveAttendees: number = 0;
      let automatedAttendees: number = 0;

      if (parts[0] === "All") {
        extractedPlanType = PLAN_NAMES.ALL_IN_ONE; // "All-In-One"
        currency = parts[3]; // USD
        duration = parts[4]; // Yearly/Monthly
        extractedAttendees = parseInt(parts[5]); // e.g., 750

        // Find the matching plan to get the label
        const selectedPlan = LIVE_AUTO_PRICES.find(
          (plan) => plan.value === extractedAttendees.toString()
        );
        if (selectedPlan) {
          // Parse the label to extract live and automated attendees, handling comma-separated numbers
          const labelMatch = selectedPlan.label.match(
            /([\d,]+)\s+Live\s+&\s+([\d,]+)\s+Automated/
          );
          if (labelMatch) {
            // Remove commas and convert to numbers
            liveAttendees = parseInt(labelMatch[1].replace(/,/g, ""));
            automatedAttendees = parseInt(labelMatch[2].replace(/,/g, ""));
          }
        }
      } else {
        extractedPlanType = parts[0] + "-" + parts[1]; // Could be LIVE_WEBINARS or AUTOMATED_GOLD
        currency = parts[2]; // USD
        duration = parts[3]; // Yearly/Monthly
        extractedAttendees = parseInt(parts[4]); // e.g., 250
      }

      // Validate that the extracted plan type is one of the valid plan names
      const validPlanTypes = [
        PLAN_NAMES.LIVE_WEBINARS,
        PLAN_NAMES.AUTOMATED_GOLD,
        PLAN_NAMES.ALL_IN_ONE,
      ];

      if (!validPlanTypes.includes(extractedPlanType)) {
        console.log(
          "Invalid plan type:",
          extractedPlanType,
          "redirecting to pricing page"
        );
        window.location.href = "https://easywebinar.com/pricing/";
        return;
      }

      // Get pricing data based on plan type
      let pricingData: PricingTier[];
      let selectedPlan: PricingTier | undefined;

      switch (extractedPlanType) {
        case PLAN_NAMES.LIVE_WEBINARS:
          pricingData = LIVE_PRICES;
          selectedPlan = pricingData.find(
            (plan: PricingTier) => plan.value === extractedAttendees.toString()
          );
          break;
        case PLAN_NAMES.AUTOMATED_GOLD:
          pricingData = AUTOMATED_PRICES;
          selectedPlan = pricingData.find(
            (plan: PricingTier) => plan.value === extractedAttendees.toString()
          );
          break;
        case PLAN_NAMES.ALL_IN_ONE:
          pricingData = LIVE_AUTO_PRICES;
          selectedPlan = pricingData.find(
            (plan: PricingTier) => plan.value === extractedAttendees.toString()
          );
          break;
        default:
          console.log("Unknown plan type:", extractedPlanType);
          return;
      }

      if (!selectedPlan) {
        console.log(
          "No matching plan found for attendees:",
          extractedAttendees
        );
        return;
      }

      // Update the features object
      const features = {
        planType: extractedPlanType,
        currency,
        duration,
        attendees: extractedAttendees ?? 0,
        liveAttendees: liveAttendees,
        automatedAttendees: automatedAttendees,
        price:
          duration === "Yearly"
            ? selectedPlan.annual ?? 0
            : selectedPlan.monthly ?? 0,
        storage: selectedPlan.storage,
        webinars: "Unlimited",
      };

      // Get plan-specific features based on plan type
      let planFeatures = [];
      switch (extractedPlanType) {
        case PLAN_NAMES.LIVE_WEBINARS:
          planFeatures = [
            `Up to ${extractedAttendees} attendees`,
            "Unlimited live webinars",
            "Custom landing pages + smart scheduling",
            `Up to ${selectedPlan.storage} storage space`,
            "Share offers, files & links",
            "Polls, Q&A, and emoji reactions",
          ];
          break;
        case PLAN_NAMES.AUTOMATED_GOLD:
          planFeatures = [
            `Up to ${extractedAttendees} attendees`,
            "Unlimited automated events",
            "Just-in-time scheduling",
            "Preloaded + live chats & real-time replays",
            "Automated, segmented email campaigns",
            "Data export & analytics",
          ];
          break;
        case PLAN_NAMES.ALL_IN_ONE:
          planFeatures = [
            `Up to ${extractedAttendees} total attendees`,
            "Unlimited live & automated webinars",
            "Turn replays into automated webinars",
            "Auto-played polls, CTAs, and chat prompts",
            "Priority support & success onboarding",
            "CRM Tool Integrations",
          ];
          break;
      }

      // Update all state variables at once to prevent multiple re-renders
      setPlanFeatures(features);
      setPlanType(extractedPlanType);
      setLeads(extractedAttendees);
      setLiveAttendees(liveAttendees);
      setAutomatedAttendees(automatedAttendees);
      setPlanPrice(
        duration === "Yearly"
          ? selectedPlan.annual ?? 0
          : selectedPlan.monthly ?? 0
      );
      setPlanWebinars("Unlimited");
      setPlanStorage(selectedPlan.storage);
    }
  }, [planId]);

  const calculateDynamicPricing = () => {
    if (isLoadingPlan || !planDetails) {
      return {
        currentPrice: "$00.00",
        alternativePrice: "$00.00",
        savings: "$00.00",
        savingsPercentage: 0,
        isYearlySelected: isYearly,
        currentPeriod: isYearly ? "year" : "month",
        alternativePeriod: isYearly ? "month" : "year",
      };
    }

    const quantity = planId ? parseInt(planId.match(/-(\d+)$/)?.[1] || "0") : 0;

    if (planId === PLAN_NAMES.FREE_TRIAL) {
      return {
        currentPrice: "$00.00",
        alternativePrice: "$00.00",
        savings: "$00.00",
        savingsPercentage: 0,
        isYearlySelected: isYearly,
        currentPeriod: isYearly ? "year" : "month",
        alternativePeriod: isYearly ? "month" : "year",
      };
    }

    // Find the current plan tier
    const currentTier = planDetails?.tiers.find((tier: any) => {
      if (!tier.ending_unit) {
        return quantity >= tier.starting_unit;
      }
      return quantity >= tier.starting_unit && quantity <= tier.ending_unit;
    });

    if (!currentTier) {
      return {
        currentPrice: "$00.00",
        alternativePrice: "$00.00",
        savings: "$00.00",
        savingsPercentage: 0,
        isYearlySelected: isYearly,
        currentPeriod: isYearly ? "year" : "month",
        alternativePeriod: isYearly ? "month" : "year",
      };
    }

    // Get current plan price
    const currentPrice = currentTier.price / 100; // Convert cents to dollars
    let discountedCurrentPrice = currentPrice;

    // Apply discount if promo code is applied
    if (appliedPromo) {
      if (appliedPromo.type === "percentage") {
        discountedCurrentPrice =
          currentPrice * (1 - appliedPromo.discount / 100);
      } else if (appliedPromo.type === "fixed_amount") {
        discountedCurrentPrice = Math.max(
          0,
          currentPrice - appliedPromo.discount / 100
        );
      }
    }

    // Calculate alternative pricing
    let alternativePrice = 0;
    let alternativeTier = null;

    if (isYearly) {
      // If yearly is selected, calculate monthly equivalent
      if (monthlyPlanDetails) {
        // Use the fetched monthly plan details
        alternativeTier = monthlyPlanDetails?.tiers.find((tier: any) => {
          if (!tier.ending_unit) {
            return quantity >= tier.starting_unit;
          }
          return quantity >= tier.starting_unit && quantity <= tier.ending_unit;
        });

        if (alternativeTier) {
          alternativePrice = alternativeTier.price / 100; // Convert cents to dollars

          // Apply same discount to alternative price
          if (appliedPromo) {
            if (appliedPromo.type === "percentage") {
              alternativePrice =
                alternativePrice * (1 - appliedPromo.discount / 100);
            } else if (appliedPromo.type === "fixed_amount") {
              alternativePrice = Math.max(
                0,
                alternativePrice - appliedPromo.discount / 100
              );
            }
          }

          // Calculate yearly equivalent of monthly price
          alternativePrice = alternativePrice * 12;
        } else {
          // Fallback: estimate monthly price as 1/12 of yearly price
          alternativePrice = currentPrice;
        }
      } else {
        // Fallback: estimate monthly price as 1/12 of yearly price
        alternativePrice = currentPrice;
      }
    } else {
      // If monthly is selected, calculate yearly equivalent
      if (monthlyPlanDetails) {
        // Use the fetched monthly plan details
        alternativeTier = monthlyPlanDetails?.tiers.find((tier: any) => {
          if (!tier.ending_unit) {
            return quantity >= tier.starting_unit;
          }
          return quantity >= tier.starting_unit && quantity <= tier.ending_unit;
        });

        if (alternativeTier) {
          alternativePrice = alternativeTier.price / 100; // Convert cents to dollars

          // Apply same discount to alternative price
          if (appliedPromo) {
            if (appliedPromo.type === "percentage") {
              alternativePrice =
                alternativePrice * (1 - appliedPromo.discount / 100);
            } else if (appliedPromo.type === "fixed_amount") {
              alternativePrice = Math.max(
                0,
                alternativePrice - appliedPromo.discount / 100
              );
            }
          }

          // Calculate yearly equivalent of monthly price
          alternativePrice = alternativePrice * 12;
        } else {
          // Fallback: estimate yearly price as 12x monthly price
          alternativePrice = currentPrice * 12;
        }
      } else {
        // Fallback: estimate yearly price as 12x monthly price
        alternativePrice = currentPrice * 12;
      }
    }

    // Calculate static monthly and yearly prices
    let monthlyPrice = 0;
    let yearlyPrice = 0;

    // Calculate monthly price
    if (isYearly) {
      // If yearly is selected, use the monthly plan details
      if (monthlyPlanDetails) {
        const monthlyTier = monthlyPlanDetails?.tiers.find((tier: any) => {
          if (!tier.ending_unit) {
            return quantity >= tier.starting_unit;
          }
          return quantity >= tier.starting_unit && quantity <= tier.ending_unit;
        });

        if (monthlyTier) {
          monthlyPrice = monthlyTier.price / 100; // Convert cents to dollars
          // Apply discount if promo code is applied
          if (appliedPromo) {
            if (appliedPromo.type === "percentage") {
              monthlyPrice = monthlyPrice * (1 - appliedPromo.discount / 100);
            } else if (appliedPromo.type === "fixed_amount") {
              monthlyPrice = Math.max(
                0,
                monthlyPrice - appliedPromo.discount / 100
              );
            }
          }
        }
      }
    } else {
      // If monthly is selected, use the current plan details
      if (planDetails && planDetails.periodUnit === "month") {
        const monthlyTier = planDetails?.tiers.find((tier: any) => {
          if (!tier.ending_unit) {
            return quantity >= tier.starting_unit;
          }
          return quantity >= tier.starting_unit && quantity <= tier.ending_unit;
        });

        if (monthlyTier) {
          monthlyPrice = monthlyTier.price / 100; // Convert cents to dollars
          // Apply discount if promo code is applied
          if (appliedPromo) {
            if (appliedPromo.type === "percentage") {
              monthlyPrice = monthlyPrice * (1 - appliedPromo.discount / 100);
            } else if (appliedPromo.type === "fixed_amount") {
              monthlyPrice = Math.max(
                0,
                monthlyPrice - appliedPromo.discount / 100
              );
            }
          }
        }
      }
    }

    // Calculate yearly price (monthly equivalent)
    if (isYearly) {
      // If yearly is selected, use the current yearly price
      yearlyPrice = discountedCurrentPrice / 12;
    } else {
      // If monthly is selected, use the actual yearly price from alternative plan details
      if (monthlyPlanDetails && monthlyPlanDetails.periodUnit === "year") {
        // Use the fetched yearly plan details
        const yearlyTier = monthlyPlanDetails?.tiers.find((tier: any) => {
          if (!tier.ending_unit) {
            return quantity >= tier.starting_unit;
          }
          return quantity >= tier.starting_unit && quantity <= tier.ending_unit;
        });

        if (yearlyTier) {
          let yearlyPriceValue = yearlyTier.price / 100; // Convert cents to dollars
          // Apply discount if promo code is applied
          if (appliedPromo) {
            if (appliedPromo.type === "percentage") {
              yearlyPriceValue =
                yearlyPriceValue * (1 - appliedPromo.discount / 100);
            } else if (appliedPromo.type === "fixed_amount") {
              yearlyPriceValue = Math.max(
                0,
                yearlyPriceValue - appliedPromo.discount / 100
              );
            }
          }
          yearlyPrice = yearlyPriceValue / 12;
        } else {
          // Fallback: estimate yearly price as 12x monthly price
          yearlyPrice = discountedCurrentPrice;
        }
      } else {
        // Fallback: estimate yearly price as 12x monthly price
        yearlyPrice = discountedCurrentPrice;
      }
    }

    // Calculate savings
    let savings = 0;
    let savingsPercentage = 0;

    if (isYearly) {
      // If yearly is selected, savings = monthly equivalent - yearly price
      // Use original prices for savings calculation
      const originalAlternativePrice =
        alternativePrice / (appliedPromo ? 1 - appliedPromo.discount / 100 : 1);
      const originalCurrentPrice = currentPrice;
      savings = Math.abs(originalAlternativePrice - originalCurrentPrice);

      // Add promo code discount to savings if applied
      if (appliedPromo) {
        if (appliedPromo.type === "percentage") {
          savings += (originalCurrentPrice * appliedPromo.discount) / 100;
        } else if (appliedPromo.type === "fixed_amount") {
          savings += appliedPromo.discount / 100;
        }
      }

      savingsPercentage =
        originalAlternativePrice > 0
          ? Math.round((savings / originalAlternativePrice) * 100)
          : 0;
    } else {
      // If monthly is selected, savings = (monthly price × 12) - actual yearly price
      // Use original prices for savings calculation
      const originalMonthlyPrice =
        monthlyPrice / (appliedPromo ? 1 - appliedPromo.discount / 100 : 1);
      const yearlyEquivalent = originalMonthlyPrice * 12;
      const actualYearlyPrice = monthlyPlanDetails
        ? (monthlyPlanDetails.tiers.find((tier: any) => {
          if (!tier.ending_unit) {
            return quantity >= tier.starting_unit;
          }
          return (
            quantity >= tier.starting_unit && quantity <= tier.ending_unit
          );
        })?.price || 0) / 100
        : 0;

      savings = Math.abs(yearlyEquivalent - actualYearlyPrice);

      // Add promo code discount to savings if applied
      if (appliedPromo) {
        if (appliedPromo.type === "percentage") {
          savings += (actualYearlyPrice * appliedPromo.discount) / 100;
        } else if (appliedPromo.type === "fixed_amount") {
          savings += appliedPromo.discount / 100;
        }
      }

      savingsPercentage =
        actualYearlyPrice > 0
          ? Math.round((savings / actualYearlyPrice) * 100)
          : 0;
    }

    return {
      currentPrice: `$${Math.round(discountedCurrentPrice)}`,
      originalPrice: `$${Math.round(currentPrice)}`,
      alternativePrice: `$${Math.round(alternativePrice)}`,
      savings: `$${Math.round(savings)}`,
      savingsPercentage,
      isYearlySelected: isYearly,
      currentPeriod: isYearly ? "year" : "month",
      alternativePeriod: isYearly ? "month" : "year",
      currentPriceValue: discountedCurrentPrice || 0,
      originalPriceValue: currentPrice || 0,
      alternativePriceValue: alternativePrice || 0,
      monthlyPrice: `$${Math.round(monthlyPrice)}`,
      yearlyPrice: `$${Math.round(yearlyPrice)}`,
    };
  };

  const validatePassword = (password: string) => {
    const minLength = 8;
    const hasNumber = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    const hasLowercase = /[a-z]/.test(password);
    const hasUppercase = /[A-Z]/.test(password);

    if (password.length < minLength) {
      return "Password must be at least 8 characters long";
    }
    if (!hasNumber) {
      return "Password must contain at least one number";
    }
    if (!hasSpecialChar) {
      return "Password must contain at least one special character";
    }
    if (!hasUppercase) {
      return "Password must contain at least one uppercase letter";
    }
    if (!hasLowercase) {
      return "Password must contain at least one lowercase letter";
    }
    return "";
  };

  const validateForm = () => {
    const errors = {
      email: "",
      password: "",
      subdomain: "",
    };
    let isValid = true;

    // Check required fields
    const emailValidation = validateEmail(formData.email);
    if (!emailValidation.isValid) {
      errors.email = emailValidation.error || "Please enter a valid email";
      isValid = false;
    }

    // Password validation
    if (!formData.password.trim()) {
      errors.password = "Password is required";
      setPasswordError("Password is required");
      isValid = false;
    } else {
      const passwordValidationError = validatePassword(formData.password);
      if (passwordValidationError) {
        errors.password = passwordValidationError;
        setPasswordError(passwordValidationError);
        isValid = false;
      } else {
        setPasswordError("");
      }
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleTermsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAcceptedTerms(event.target.checked);
    if (event.target.checked) {
      setTermsError("");
    }
  };

  const [verificationDialogOpen, setVerificationDialogOpen] =
    React.useState(false);

  const handleVerificationDialogOpen = () => {
    setVerificationDialogOpen(true);
  };

  const handleVerificationDialogClose = async () => {
    setVerificationDialogOpen(false);
    const fullPlanName =
      planId === PLAN_NAMES.FREE_TRIAL || planId === "free-trial-plan"
        ? PLAN_NAMES.FREE_TRIAL
        : (planId ?? selectedPlanId ?? "");
    const contactIdToUpdate = ghlContactId || (await GhlService.getContactByEmail(formData.email).then((data: any) => data?.id));
    if (contactIdToUpdate) {
      await GhlService.updateContactFields({
        contactId: contactIdToUpdate,
        fields: [
          { key: "plan_name", value: fullPlanName },
          { key: "subscription_status", value: "In-Trial" },
        ],
      });
      await GhlService.addTags(contactIdToUpdate, [fullPlanName]);
    }
    const leadData = {
      leadId: leadSquaredId,
      fields: [
        { Attribute: "mx_Plan_Name", Value: fullPlanName },
        { Attribute: "mx_Status", Value: "Pending Verification" },
      ],
    };
    await LeadsquaredService.updateLsField(leadData);
    navigate("/login");
  };

  // Helper function to clear UTM params from localStorage
  const clearUtmParamsFromStorage = () => {
    localStorage.removeItem("utm_source");
    localStorage.removeItem("utm_medium");
    localStorage.removeItem("utm_campaign");
    localStorage.removeItem("utm_content");
    localStorage.removeItem("utm_term");
    localStorage.removeItem("ad_id");
    localStorage.removeItem("fbc_id");
    localStorage.removeItem("h_ad_id");
    localStorage.removeItem("utm_id");
    localStorage.removeItem("gc_id");
    localStorage.removeItem("h_ga_id");
    localStorage.removeItem("h_keyword_id");
    localStorage.removeItem("h_keyword");
    localStorage.removeItem("h_placement");
    localStorage.removeItem("g_special_campaign");
  };

  const handleVerificationDialogProceed = async () => {
    setVerificationDialogOpen(false);
    const fullPlanName =
      planId === PLAN_NAMES.FREE_TRIAL || planId === "free-trial-plan"
        ? PLAN_NAMES.FREE_TRIAL
        : (planId ?? selectedPlanId ?? "");
    try {
      if (ghlContactId) {
        await GhlService.addTags(ghlContactId, ["email verified"]);
        await GhlService.addTags(ghlContactId, [fullPlanName]);
        await GhlService.updateContactFields({
          contactId: ghlContactId,
          fields: [
            { key: "plan_name", value: fullPlanName },
            { key: "subscription_status", value: "In-Trial" },
          ],
        });
      }
      await LeadsquaredService.joinActivity({
        id: leadSquaredId,
        activityEvent: 205,
        activityValue: "Email Verified",
      });
      const leadData = {
        leadId: leadSquaredId,
        fields: [
          {
            Attribute: "mx_Plan_Name",
            Value: fullPlanName,
          },
          { Attribute: "mx_Status", Value: "Active" },
        ],
      };
      await LeadsquaredService.updateLsField(leadData);
    } catch (error) {
      console.error("Error updating lead data:", error);
    }

    // Clear UTM parameters from localStorage after successful signup
    clearUtmParamsFromStorage();

    // Pass plan data through URL parameters
    const planData = {
      price: planPrice,
      currency: "USD",
      coupon: appliedPromo?.code || "",
      plan_name: planDetails?.name || selectedPlanId || "Unknown Plan",
      customer_type: "with_cc",
    };

    // Encode plan data as URL parameters
    const planDataParams = new URLSearchParams({
      ...planData,
      price: planData.price.toString(),
      ...(isEbookFlow && { flow: "e-book" }),
    }).toString();

    window.location.href = `/loading-dashboard?ew_email=${formData.email
      }&planId=${isTrial ? PLAN_NAMES.FREE_TRIAL : planId}&${planDataParams}`;
  };

  const BpIcon = styled("span")(({ theme }) => ({
    borderRadius: 4,
    width: 24,
    height: 24,
    boxShadow:
      "inset 0 0 0 1px rgba(16,22,26,.2), inset 0 -1px 0 rgba(16,22,26,.1)",
    backgroundColor: "#fff",

    "input:hover ~ &": {
      border: "1px solid #666666",
    },
  }));

  const BpCheckedIcon = styled(BpIcon)({
    "&::before": {
      display: "block",
      width: 24,
      height: 24,
      backgroundImage:
        "url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath" +
        " fillRule='evenodd' clipRule='evenodd' d='M12 5c-.28 0-.53.11-.71.29L7 9.59l-2.29-2.3a1.003 " +
        "1.003 0 00-1.42 1.42l3 3c.18.18.43.29.71.29s.53-.11.71-.29l5-5A1.003 1.003 0 0012 5z' fill='%23fff'/%3E%3C/svg%3E\")",
      content: '""',
    },
    "input ~ &": {
      backgroundColor: "#178EFC",
      border: "none",
    },

    "input:hover ~ &": {
      border: "none",
    },
  });

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    // Only allow changing to the second tab (newValue = 1) if it's done programmatically
    // Users can always go back to the first tab (newValue = 0)
    if (newValue === 0) {
      setValue(newValue);
    }
    // The second tab (newValue = 1) can only be reached through handleNext function
    // which validates the form fields first
  };

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  const handleMouseUpPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  const handleSocialSignIn = (provider: SocialProvider) => {
    try {
      const authUrl = config.sso_auth_url;
      const planIdParam = searchParams.get("planId");

      const utmParams = {
        utm_source: localStorage.getItem("utm_source"),
        utm_medium: localStorage.getItem("utm_medium"),
        utm_campaign: localStorage.getItem("utm_campaign"),
        utm_content: localStorage.getItem("utm_content"),
        utm_term: localStorage.getItem("utm_term"),
        ad_id: localStorage.getItem("ad_id"),
        fbc_id: localStorage.getItem("fbc_id"),
        h_ad_id: localStorage.getItem("h_ad_id"),
        utm_id: localStorage.getItem("utm_id"),
        gc_id: localStorage.getItem("gc_id"),
        h_ga_id: localStorage.getItem("h_ga_id"),
        h_keyword_id: localStorage.getItem("h_keyword_id"),
        h_keyword: localStorage.getItem("h_keyword"),
        h_placement: localStorage.getItem("h_placement"),
        g_special_campaign: localStorage.getItem("g_special_campaign"),
      };

      // Filter out null/undefined/empty values from UTM params
      const cleanUtmParams = Object.entries(utmParams)
        .filter(
          ([_, value]) =>
            value !== null && value !== undefined && value.trim() !== ""
        )
        .reduce((acc, [key, value]) => ({ ...acc, [key]: value?.trim() }), {});

      // Create state parameter with additional metadata
      const stateData = {
        isRegistration: true,
        timestamp: Date.now(),
        flow: "first-flow",
        ...(planIdParam && { planId: planIdParam }), // Only include planId if it exists
        ...(Object.keys(cleanUtmParams).length > 0 && {
          utm_params: cleanUtmParams,
        }), // Only include UTM params if they exist
      };

      const baseParams = {
        client_id: config.sso_client_id,
        response_type: "code",
        scope: "email openid profile phone",
        redirect_uri: `${window.location.origin}/auth/sso/callback`,
        state: JSON.stringify(stateData),
        prompt: "select_account",
      };

      const params = new URLSearchParams({
        ...baseParams,
        identity_provider: provider,
      });

      window.location.href = `${authUrl}?${params.toString()}`;
    } catch (error) {
      console.error("Google Sign In Error:", error);
      toast.error("Failed to initialize Google Sign In");
    }
  };


  // Execute first step processing when email comes from URL parameter
  const executeFirstStepProcessing = async (email: string) => {
    try {
      console.log("Starting first step processing for email:", email);
      setIsLoading(true);

      // Check if email exists
      console.log("Checking if email exists...");
      const emailCheckResponse = await AuthService.checkEmailExists(email);
      if (emailCheckResponse.data?.exists) {
        console.log("Email already exists, setting account exists state");
        setValue(1);
        setAccountExists(true);
        setIsLoading(false);
        return;
      }
      console.log("Email does not exist, proceeding with processing");

      // Auto-generate firstName and lastName from email
      const firstName = generateFirstName(email);
      const lastName = "";

      // Generate subdomain from first name
      const baseSubdomain = firstName.toLowerCase().trim().replace(/\s+/g, "");
      const subdomainResponse = await AuthService.generateSubdomain(
        baseSubdomain,
        Applications.EASYWEBINAR
      );

      // Store the generated subdomain for use in step 2
      setFormData((prev) => ({
        ...prev,
        subdomain: subdomainResponse.subdomain,
      }));

      // Log signup event before moving to next step
      try {
        const planId = searchParams.get("planId");
        const cleanPlanIdValue = planId
          ? planId.replace(/-\d+$/, "")
          : undefined;
        const eventData = {
          step: SIGNUP_STEPS.FORM,
          password: formData.password,
          planId: cleanPlanIdValue || "",
          planUnits: planId ? planId.match(/-(\d+)$/)?.[1] || 10 : 10,
          signupFlow: SIGNUP_FLOWS.PRICING_PAGE,
        };

        await logEvent(email, EVENT_TYPES.SIGNUP, eventData);
      } catch (error: any) {
        console.error("Error logging signup event:", error);
        // Don't block the flow if event logging fails
      }

      // Create lead in Leadsquared
      try {
        let utm_Data = {
          utm_source: localStorage.getItem("utm_source"),
          utm_medium: localStorage.getItem("utm_medium"),
          utm_campaign: localStorage.getItem("utm_campaign"),
          utm_content: localStorage.getItem("utm_content"),
          utm_term: localStorage.getItem("utm_term"),
          ad_id: localStorage.getItem("ad_id"),
          fbc_id: localStorage.getItem("fbc_id"),
          h_ad_id: localStorage.getItem("h_ad_id"),
          utm_id: localStorage.getItem("utm_id"),
          gc_id: localStorage.getItem("gc_id"),
          h_ga_id: localStorage.getItem("h_ga_id"),
          h_keyword_id: localStorage.getItem("h_keyword_id"),
          h_keyword: localStorage.getItem("h_keyword"),
          h_placement: localStorage.getItem("h_placement"),
          g_special_campaign: localStorage.getItem("g_special_campaign"),
        };

        // Create form data with generated firstName and lastName
        const formDataWithDetails = {
          firstName,
          lastName,
          email: email,
          password: formData.password,
          timezone: getTimezoneString(),
          subdomain: subdomainResponse.subdomain,
        };

        const ghlContactId = await GhlService.createContact(formDataWithDetails, "", utm_Data);
        if (ghlContactId) {
          setGhlContactId(ghlContactId);
          await GhlService.addTags(ghlContactId, ["cc trial user", "Paid Trial"]);
        }

        const LsId = await LeadsquaredService.createtLead(
          formDataWithDetails,
          "",
          utm_Data
        );
        await LeadsquaredService.joinActivity({
          id: LsId,
          activityEvent: 201,
          activityValue: "Signup Attempt - Ad funnels",
        });
        const leadData = {
          leadId: LsId,
          fields: [
            { Attribute: "mx_Source_lp", Value: searchParams.get("Source_IP") },
          ],
        };
        await LeadsquaredService.updateLsField(leadData);
        setLeadSquaredId(LsId);
      } catch (error: any) {
        console.error("Error creating lead:", error);
      }
      console.log("First step processing completed successfully");
      // Move to step 1 after successful processing
      setValue(1);
    } catch (error: any) {
      console.error("Error in first step processing:", error);
      toast.error("Failed to process email. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleGetStarted = async () => {
    // Add domain validation here
    if (!formData.subdomain.trim()) {
      setFormErrors((prev) => ({
        ...prev,
        subdomain: "Domain is required",
      }));
      setDomainSuggestions([]);
      return;
    } else if (!/^[a-zA-Z0-9-]+$/.test(formData.subdomain)) {
      setFormErrors((prev) => ({
        ...prev,
        subdomain: "Domain can only contain letters, numbers, and hyphens",
      }));
      setDomainSuggestions([]);
      return;
    }

    try {
      setIsLoading(true);

      // Check domain availability first
      const domainResponse = await AuthService.checkSubDomainAvailability(
        formData.subdomain,
        Applications.EASYWEBINAR
      );

      if (!domainResponse.available) {
        setFormErrors((prev) => ({
          ...prev,
          subdomain: "This domain is already taken. Please try another one.",
        }));
        // Set domain suggestions if they exist
        if (
          domainResponse.suggestions &&
          domainResponse.suggestions.length > 0
        ) {
          setDomainSuggestions(domainResponse.suggestions);
        } else {
          setDomainSuggestions([]);
        }
        setIsLoading(false);
        return;
      }

      // Clear suggestions if domain is available
      setDomainSuggestions([]);
      setIsLoading(false);

      // Show upgrade modal instead of proceeding directly
      setShowUpgradeModal(true);
      return;
    } catch (error: any) {
      console.error("Error checking domain:", error);
      toast.error("Error checking domain availability. Please try again.");
      setIsLoading(false);
    }
  };

  const handleProceedWithSignup = async (selectedPlanId: PlanId) => {
    try {
      setIsLoading(true);
      setSelectedPlanId(selectedPlanId);
      setIsLoadingUpgrade((prev) => ({
        ...prev,
        accept: selectedPlanId.includes("Yearly"),
        decline: selectedPlanId.includes("Monthly"),
      }));

      let cardTokenResult = null;

      // Process card for the selected plan (not a free trial)
      if (selectedPlanId && !isTrial) {
        // Check if card has any validation errors
        if (cardError) {
          toast.error("Please fix card validation errors before proceeding");
          setIsLoading(false);
          setIsLoadingUpgrade({ accept: false, decline: false });
          return;
        }

        const result = await cardRef.current.tokenize();

        if (result.error) {
          setCardError(result.error.message || "Card tokenization failed");
          toast.error("Please check your card details.");
          setIsLoading(false);
          setIsLoadingUpgrade({ accept: false, decline: false });
          return;
        }

        cardTokenResult = result.token;
        setCardToken(cardTokenResult);
        setCardError(null);

        if (!cardTokenResult) {
          setCardError("Please enter valid card details");
          toast.error("Please enter valid card details");
          setIsLoading(false);
          setIsLoadingUpgrade({ accept: false, decline: false });
          return;
        }
      }

      // Get UTM parameters directly from URL
      const urlParams = new URLSearchParams(window.location.search);

      const utmParams = {
        utm_source: urlParams.get("utm_source"),
        utm_medium: urlParams.get("utm_medium"),
        utm_campaign: urlParams.get("utm_campaign"),
        utm_content: urlParams.get("utm_content"),
        utm_term: urlParams.get("utm_term"),
        ad_id: urlParams.get("ad_id"),
        fbc_id: urlParams.get("fbc_id"),
        h_ad_id: urlParams.get("h_ad_id"),
        utm_id: urlParams.get("utm_id"),
        gc_id: urlParams.get("gc_id"),
        h_ga_id: urlParams.get("h_ga_id"),
        h_keyword_id: urlParams.get("h_keyword_id"),
        h_keyword: urlParams.get("h_keyword"),
        h_placement: urlParams.get("h_placement"),
        g_special_campaign: urlParams.get("g_special_campaign"),
      };

      // Remove null/undefined values from the UTM params object
      const cleanUtmParams = Object.entries(utmParams)
        .filter(([_, value]) => value !== null && value !== undefined)
        .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

      // Check if buyNow parameter is present and true
      const buyNow = urlParams.get("BuyNow") == "true";

      // Calculate trialEnd: 14 days from now (Unix timestamp in seconds)
      // If buyNow is true, set to 0 (no trial), otherwise set to 14 days from now
      const trialEnd = buyNow
        ? 0
        : Math.floor(Date.now() / 1000) + 14 * 24 * 60 * 60;

      // Generate firstName and lastName from email
      const firstName = generateFirstName(formData.email);
      const lastName = "";

      const signupData = {
        ...formData,
        firstName,
        lastName,
        phoneNumber: "",
        timezone: getTimezoneString(),
        ...(selectedPlanId &&
          !isTrial && {
          planId: selectedPlanId,
          planUnits: isProfessionalPlan ? 200 : 50,
          cardToken: cardTokenResult,
          couponId: selectedPlanId === "All-In-One-USD-Yearly" ? "SALESEXPERIENCEYEARLY" : appliedPromo?.code,
        }),
        ...(isTrial && {
          planId: selectedPlanId,
        }),
        // Set trialEnd: 0 if buyNow is true, otherwise 14 days from now
        ...((selectedPlanId || isTrial) && { trialEnd }),
        // Include UTM parameters if they exist
        ...(Object.keys(cleanUtmParams).length > 0 && {
          utm_params: cleanUtmParams,
        }),
      };

      const response = await AuthService.signup(signupData);

      setVerificationData({
        companyId: response.data.companyId || "",
        companyName: response.data.companyName || "",
        infLeadSquaredId: response.data.inf_lead_squared_id || "",
      });
      handleVerificationDialogOpen();

      const DigitalBookItemPriceId = "Digital-Book-USD";
      const TrialAccessItemPriceId = "Trial-Charge-USD"; // Item price ID for $1 trial access

      if (response.data.companyId && isEbookFlow) {
        // Prepare charges array
        const charges = [
          // Always charge $1 for trial access when in ebook flow
          chargebeeService.quickCharge(
            response.data.companyId,
            TrialAccessItemPriceId,
            1
          )
        ];

        // Add ebook charge if checkbox is checked
        if (includeEbook) {
          charges.push(
            chargebeeService.quickCharge(
              response.data.companyId,
              DigitalBookItemPriceId,
              1
            )
          );
        }

        // Execute all charges concurrently
        await Promise.all(charges);
      }
      // Clear UTM params on successful signup
      clearUtmParamsFromStorage();
      toast.success("Signup successful! Please verify your account.");
      try {
        await LeadsquaredService.joinActivity({
          id: leadSquaredId,
          activityEvent: 205,
          activityValue: "Waiting To Confirm Email Validation",
        });
        // if(includeEbook) {
        //   await LeadsquaredService.joinActivity({
        //     id: leadSquaredId,
        //     activityEvent: 205,
        //     activityValue: "Sales Experience",
        //     activityNote: "Digital Book Purchased - Payment Succeeded ($7.00)"
        // });
        // }
        if (selectedPlanId === "free-trial-plan") {
          if (ghlContactId) await GhlService.addTags(ghlContactId, ["Free Plan"]);
          await LeadsquaredService.joinActivity({
            id: leadSquaredId,
            activityEvent: 205,
            activityValue: "Free Plan",
          });
        } else {
          if (ghlContactId) {
            const fullPlanName =
              planId === PLAN_NAMES.FREE_TRIAL || planId === "free-trial-plan"
                ? PLAN_NAMES.FREE_TRIAL
                : (planId ?? selectedPlanId ?? "");
            const tags = ["Paid Trial", "cc trial user", fullPlanName, ...(isEbookFlow && includeEbook ? ["Sales Experience Book"] : [])];
            await GhlService.addTags(ghlContactId, tags);
            await GhlService.updateContactFields({
              contactId: ghlContactId,
              fields: [
                { key: "plan_name", value: fullPlanName },
                { key: "subscription_status", value: "In-Trial" },
              ],
            });
          }
          await Promise.all([
            LeadsquaredService.joinActivity({
              id: leadSquaredId,
              activityEvent: 205,
              activityValue: isTrial ? PLAN_NAMES.FREE_TRIAL : (planId ?? selectedPlanId),
            }),
            LeadsquaredService.joinActivity({
              id: leadSquaredId,
              activityEvent: 205,
              activityValue: "$1 Paid Trial",
              activityNote: "14-day trial",
            }),
            LeadsquaredService.joinActivity({
              id: leadSquaredId,
              activityEvent: 205,
              activityValue: "Currently In Trial",
            }),
          ]);
        }
      } catch (error: any) {
        console.error("Error joining activities:", error);
      }
    } catch (error: any) {
      console.error("Signup failed:", error);
      setDomainSuggestions([]);
      // Clear UTM params on signup failure too
      clearUtmParamsFromStorage();
      if (error.name === "INVALID_CARD") {
        setCardError("Please enter valid card details");
        toast.error("Invalid card details. Please try again.");
      } else {
        toast.error(
          error.message || "An error occurred during signup. Please try again."
        );
      }
    } finally {
      setIsLoading(false);
      setIsLoadingUpgrade({ accept: false, decline: false });
    }
  };

  // const validateCoupon = async () => {
  //   if (!promoCode.trim()) {
  //     setPromoError("Please enter a promo code");
  //     toast.error("Please enter a promo code");
  //     return;
  //   }

  //   try {
  //     const planId = searchParams.get("planId");
  //     const response = await chargebeeService.validateCoupon(
  //       promoCode,
  //       planId ? planId.replace(/-\d+$/, "") : undefined
  //     );

  //     if (response.data.valid) {
  //       const { coupon } = response.data;
  //       setAppliedPromo({
  //         code: coupon.id,
  //         discount: coupon.discountPercentage || coupon.discountAmount,
  //         type: coupon.discountType,
  //       });
  //       setPromoError("");
  //       setShowPromoField(false);
  //       toast.success("Promo code applied successfully!");
  //     } else {
  //       setPromoError(response.data.error || "Invalid promo code");
  //       setAppliedPromo(null);
  //       toast.error(response.data.error || "Invalid promo code");
  //     }
  //   } catch (error: any) {
  //     console.error("Error validating coupon:", error);
  //     setPromoError("Failed to validate promo code");
  //     setAppliedPromo(null);
  //     toast.error("Failed to validate promo code. Please try again.");
  //   }
  // };
  
  // Inside the Signup component, add a state variable for planFeatures
  const [planFeatures, setPlanFeatures] = useState<{
    planType: string;
    currency: string;
    duration: string;
    attendees: number;
    liveAttendees?: number;
    automatedAttendees?: number;
    price: number | string;
    webinars: number | string;
    storage: string;
  } | null>(null);

  // Add new state variables to store the extracted values
  const [planType, setPlanType] = useState<string>("");
  const [leads, setLeads] = useState<number>(0);
  const [liveAttendees, setLiveAttendees] = useState<number | undefined>(
    undefined
  );
  const [automatedAttendees, setAutomatedAttendees] = useState<
    number | undefined
  >(undefined);
  const [planPrice, setPlanPrice] = useState<number | string>("");
  const [planWebinars, setPlanWebinars] = useState<number | string>("");
  const [planStorage, setPlanStorage] = useState<string>("");

  function captureUTMParams() {
    const urlParams = new URLSearchParams(window.location.search);

    const utmSource = urlParams.get("utm_source");
    const utmMedium = urlParams.get("utm_medium");
    const utmCampaign = urlParams.get("utm_campaign");
    const utmContent = urlParams.get("utm_content");
    const utmTerm = urlParams.get("utm_term");
    const email = urlParams.get("email");
    const adId = urlParams.get("ad_id");
    const fbcId = urlParams.get("fbc_id");
    const hAdId = urlParams.get("h_ad_id");
    const utmId = urlParams.get("utm_id");
    const gcId = urlParams.get("gc_id");
    const hGaId = urlParams.get("h_ga_id");
    const hKeywordId = urlParams.get("h_keyword_id");
    const hKeyword = urlParams.get("h_keyword");
    const hPlacement = urlParams.get("h_placement");
    const gSpecialCampaign = urlParams.get("g_special_campaign");

    if (utmSource) localStorage.setItem("utm_source", utmSource);
    if (utmMedium) localStorage.setItem("utm_medium", utmMedium);
    if (utmCampaign) localStorage.setItem("utm_campaign", utmCampaign);
    if (utmContent) localStorage.setItem("utm_content", utmContent);
    if (utmTerm) localStorage.setItem("utm_term", utmTerm);
    if (adId) localStorage.setItem("ad_id", adId);
    if (fbcId) localStorage.setItem("fbc_id", fbcId);
    if (hAdId) localStorage.setItem("h_ad_id", hAdId);
    if (utmId) localStorage.setItem("utm_id", utmId);
    if (gcId) localStorage.setItem("gc_id", gcId);
    if (hGaId) localStorage.setItem("h_ga_id", hGaId);
    if (hKeywordId) localStorage.setItem("h_keyword_id", hKeywordId);
    if (hKeyword) localStorage.setItem("h_keyword", hKeyword);
    if (hPlacement) localStorage.setItem("h_placement", hPlacement);
    if (gSpecialCampaign)
      localStorage.setItem("g_special_campaign", gSpecialCampaign);
    // Set email from URL parameter if present (only if not already handled by emailFromUrl logic)
    // This ensures we don't override the email if it's being processed by the emailFromUrl flow
    if (email && !emailFromUrl && value === 0) {
      setFormData((prev) => ({
        ...prev,
        email: email,
      }));
    }
  }

  // Add useEffect for capturing UTM parameters
  useEffect(() => {
    captureUTMParams();
  }, []);

  // Handle email parameter from URL - auto-fill email and execute first step processing
  useEffect(() => {
    if (emailFromUrl && !hasProcessedEmailFromUrl.current) {
      console.log("Email parameter detected in URL:", emailFromUrl);
      // Validate email format
      const emailValidation = validateEmail(emailFromUrl);
      if (emailValidation.isValid) {
        console.log(
          "Email validation passed, proceeding with first step processing"
        );
        // Update form data with email from URL
        setFormData((prev) => ({
          ...prev,
          email: emailFromUrl.trim(),
        }));

        // Clear any existing email errors
        setFormErrors((prev) => ({
          ...prev,
          email: "",
        }));

        // Mark as processed to prevent duplicate processing
        hasProcessedEmailFromUrl.current = true;

        // Execute first step processing
        executeFirstStepProcessing(emailFromUrl.trim());
      } else {
        console.error("Invalid email format in URL parameter:", emailFromUrl);
        toast.error("Invalid email format in URL parameter");
      }
    }
  }, [emailFromUrl]);

  // Remove email from URL after moving to second step
  useEffect(() => {
    if (value === 1 && emailFromUrl) {
      // Create new URL without email parameter
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete("email");

      const newUrl = `${window.location.pathname}${newSearchParams.toString() ? "?" + newSearchParams.toString() : ""
        }`;

      // Replace the current URL without triggering a page reload
      window.history.replaceState({}, "", newUrl);
    }
  }, [value, emailFromUrl, searchParams]);

  /**
   * Auto-registration logic when profileId and email are present in URL parameters
   */
  useEffect(() => {
    const handleAutoRegistration = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const profileId = urlParams.get("profileId");
      const email = urlParams.get("email");

      if (profileId && email && !isLoadingPlan && planDetails) {
        try {
          setIsAutoRegistering(true);

          // Get static profile ID
          const staticProfileId = getStaticProfileId();

          // Check if the profileId matches our static ID
          if (profileId === staticProfileId) {
            // Call getLatestSignupEvent API
            const response = await EventsLoggerService.getLatestSignupEvent(
              email
            );

            if (response.data?.event?.event_data?.password) {
              const password = response.data.event.event_data.password;
              const planId = response.data.event.event_data.planId;
              const planUnits = 10; //response.data.event.event_data.planUnits;

              // Auto-fill the form
              setFormData((prev) => ({
                ...prev,
                email: email,
                password: password,
              }));

              // Generate firstName from email
              const firstName = generateFirstName(email);

              // Proceed with automatic registration using parsed planId and units
              await performAutoRegistration(
                email,
                password,
                firstName,
                planId,
                planUnits
              );
            } else {
              console.error("Password not found in event data");
              toast.error("Unable to retrieve registration data");
            }
          } else {
            console.log(
              "Profile ID does not match, proceeding with normal flow"
            );
            console.log("Expected:", staticProfileId, "Received:", profileId);
          }
        } catch (error: any) {
          console.error("Error during auto-registration:", error);
          toast.error(
            "Auto-registration failed. Please proceed with manual signup."
          );
        } finally {
          setIsAutoRegistering(false);
        }
      }
    };

    handleAutoRegistration();
  }, [isLoadingPlan, planDetails]);

  // Function to get static profile ID
  const getStaticProfileId = () => {
    const STATIC_PROFILE_ID = "550e8400-e29b-41d4-a716-446655440000";
    return STATIC_PROFILE_ID;
  };

  // Function to perform automatic registration
  const performAutoRegistration = async (
    email: string,
    password: string,
    firstName: string,
    planId?: string,
    units?: number
  ) => {
    try {
      setIsLoading(true);

      // Generate subdomain
      const baseSubdomain = firstName.toLowerCase().trim().replace(/\s+/g, "");
      const subdomainResponse = await AuthService.generateSubdomain(
        baseSubdomain,
        Applications.EASYWEBINAR
      );

      // Create lead in LeadSquared
      const utm_Data = {
        utm_source: localStorage.getItem("utm_source"),
        utm_medium: localStorage.getItem("utm_medium"),
        utm_campaign: localStorage.getItem("utm_campaign"),
        utm_content: localStorage.getItem("utm_content"),
        utm_term: localStorage.getItem("utm_term"),
        ad_id: localStorage.getItem("ad_id"),
        fbc_id: localStorage.getItem("fbc_id"),
        h_ad_id: localStorage.getItem("h_ad_id"),
        utm_id: localStorage.getItem("utm_id"),
        gc_id: localStorage.getItem("gc_id"),
        h_ga_id: localStorage.getItem("h_ga_id"),
        h_keyword_id: localStorage.getItem("h_keyword_id"),
        h_keyword: localStorage.getItem("h_keyword"),
        h_placement: localStorage.getItem("h_placement"),
        g_special_campaign: localStorage.getItem("g_special_campaign"),
      };

      const formDataWithDetails = {
        firstName,
        lastName: "",
        email: email,
        password: password,
        timezone: getTimezoneString(),
        subdomain: subdomainResponse.subdomain,
      };

      try {
        const ghlContactId = await GhlService.createContact(formDataWithDetails, "", utm_Data);
        if (ghlContactId) {
          setGhlContactId(ghlContactId);
          await GhlService.addTags(ghlContactId, ["cc trial user", "Paid Trial"]);
        }
        const LsId = await LeadsquaredService.createtLead(
          formDataWithDetails,
          "",
          utm_Data
        );
        LeadsquaredService.joinActivity({
          id: LsId,
          activityEvent: 201,
          activityValue: "Auto Signup Attempt",
        });
        const leadData = {
          leadId: LsId,
          fields: [
            { Attribute: "mx_Source_lp", Value: searchParams.get("Source_IP") },
          ],
        };
        await LeadsquaredService.updateLsField(leadData);
        setLeadSquaredId(LsId);
      } catch (error: any) {
        console.error("Error creating lead:", error);
        return;
      }

      // Get UTM parameters from URL
      const urlParams = new URLSearchParams(window.location.search);
      const utmParams = {
        utm_source: urlParams.get("utm_source"),
        utm_medium: urlParams.get("utm_medium"),
        utm_campaign: urlParams.get("utm_campaign"),
        utm_content: urlParams.get("utm_content"),
        utm_term: urlParams.get("utm_term"),
        ad_id: urlParams.get("ad_id"),
        fbc_id: urlParams.get("fbc_id"),
        h_ad_id: urlParams.get("h_ad_id"),
        utm_id: urlParams.get("utm_id"),
        gc_id: urlParams.get("gc_id"),
        h_ga_id: urlParams.get("h_ga_id"),
        h_keyword_id: urlParams.get("h_keyword_id"),
        h_keyword: urlParams.get("h_keyword"),
        h_placement: urlParams.get("h_placement"),
        g_special_campaign: urlParams.get("g_special_campaign"),
      };

      const cleanUtmParams = Object.entries(utmParams)
        .filter(([_, value]) => value !== null && value !== undefined)
        .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

      // Check if buyNow parameter is present and true
      const buyNow = urlParams.get("BuyNow") == "true";

      // Calculate trialEnd: 14 days from now (Unix timestamp in seconds)
      // If buyNow is true, set to 0 (no trial), otherwise set to 14 days from now
      const trialEnd = buyNow
        ? 0
        : Math.floor(Date.now() / 1000) + 14 * 24 * 60 * 60;

      // Prepare signup data
      const signupData = {
        ...formDataWithDetails,
        phoneNumber: "",
        ...(planId &&
          !isTrial && {
          planId: planId || cleanPlanId, // Use planId from event data or URL
          planUnits: units || 10, // Use parsed units from event data
          couponId: appliedPromo?.code,
        }),
        ...(isTrial && {
          planId: planId || cleanPlanId, // Use planId from event data if available
        }),
        // Set trialEnd: 0 if buyNow is true, otherwise 14 days from now
        ...((planId || isTrial) && { trialEnd }),
        ...(Object.keys(cleanUtmParams).length > 0 && {
          utm_params: cleanUtmParams,
        }),
      };

      // Perform signup
      const response = await AuthService.signup(signupData);

      setVerificationData({
        companyId: response.data.companyId || "",
        companyName: response.data.companyName || "",
        infLeadSquaredId: response.data.inf_lead_squared_id || "",
      });

      // Clear UTM params on successful signup
      clearUtmParamsFromStorage();

      // Join activities
      try {
        await LeadsquaredService.joinActivity({
          id: leadSquaredId,
          activityEvent: 205,
          activityValue: "Waiting To Confirm Email Validation",
        });
        if (planDetails?.id === "free-trial-plan") {
          await LeadsquaredService.joinActivity({
            id: leadSquaredId,
            activityEvent: 205,
            activityValue: "Free Plan",
          });
        } else {
          await LeadsquaredService.joinActivity({
            id: leadSquaredId,
            activityEvent: 205,
            activityValue: isTrial ? PLAN_NAMES.FREE_TRIAL : planDetails?.id,
          });
          await LeadsquaredService.joinActivity({
            id: leadSquaredId,
            activityEvent: 205,
            activityValue: "Currently In Trial",
          });
        }
      } catch (error: any) {
        console.error("Error joining activities:", error);
      }

      logEvent(email, EVENT_TYPES.SIGNUP, {
        step: SIGNUP_STEPS.VERIFICATION,
        planId: planId,
        planUnits: units,
        autoRegistration: true,
      });

      // Open verification dialog
      handleVerificationDialogOpen();
      toast.success(
        "Auto-registration successful! Please verify your account."
      );
    } catch (error: any) {
      console.error("Auto-registration failed:", error);
      toast.error(
        error.message || "Auto-registration failed. Please try manual signup."
      );
    } finally {
      setIsLoading(false);
    }
  };
  // Check for BuyNow parameter on page load
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const buyNow = urlParams.get("BuyNow") === "true";
    setNoTrial(buyNow);
  }, []);

  // Set emailSubmitted to true if email comes from URL
  useEffect(() => {
    if (emailFromUrl && hasProcessedEmailFromUrl.current) {
      setEmailSubmitted(true);
    }
  }, [emailFromUrl]);

  // Handle email submission on the main form
  const handleEmailSubmit = async () => {
    if (!formData.email.trim()) {
      toast.error("Please enter an email address");
      return;
    }

    const emailValidation = validateEmail(formData.email);
    if (!emailValidation.isValid) {
      toast.error(emailValidation.error || "Please enter a valid email");
      return;
    }

    setIsCheckingEmail(true);
    try {
      // Check if email exists
      const emailCheckResponse = await AuthService.checkEmailExists(
        formData.email.trim()
      );

      if (emailCheckResponse.data?.exists) {
        // Account exists - show card section but with message
        setAccountExists(true);
        setValue(1);
        setEmailSubmitted(true);
      } else {
        // Email doesn't exist - use handleNext logic
        // Auto-generate firstName and lastName from email
        const firstName = generateFirstName(formData.email);
        const lastName = "";

        // Generate subdomain from first name
        const baseSubdomain = firstName.toLowerCase().trim().replace(/\s+/g, "");
        const subdomainResponse = await AuthService.generateSubdomain(
          baseSubdomain,
          Applications.EASYWEBINAR
        );

        // Store the generated subdomain
        setFormData((prev) => ({
          ...prev,
          subdomain: subdomainResponse.subdomain,
        }));

        // Log signup event
        try {
          const currentPlanId = searchParams.get("planId");
          const eventData = {
            step: SIGNUP_STEPS.FORM,
            password: formData.password,
            planId: cleanPlanId || "",
            planUnits: currentPlanId ? currentPlanId.match(/-(\d+)$/)?.[1] || 10 : 10,
            signupFlow: SIGNUP_FLOWS.PRICING_PAGE,
          };

          await logEvent(formData.email, EVENT_TYPES.SIGNUP, eventData);
        } catch (error: any) {
          console.error("Error logging signup event:", error);
        }

        // Create LeadSquared lead
        setValue(1);
        try {
          let utm_Data = {
            utm_source: localStorage.getItem("utm_source"),
            utm_medium: localStorage.getItem("utm_medium"),
            utm_campaign: localStorage.getItem("utm_campaign"),
            utm_content: localStorage.getItem("utm_content"),
            utm_term: localStorage.getItem("utm_term"),
            ad_id: localStorage.getItem("ad_id"),
            fbc_id: localStorage.getItem("fbc_id"),
            h_ad_id: localStorage.getItem("h_ad_id"),
            utm_id: localStorage.getItem("utm_id"),
            gc_id: localStorage.getItem("gc_id"),
            h_ga_id: localStorage.getItem("h_ga_id"),
            h_keyword_id: localStorage.getItem("h_keyword_id"),
            h_keyword: localStorage.getItem("h_keyword"),
            h_placement: localStorage.getItem("h_placement"),
            g_special_campaign: localStorage.getItem("g_special_campaign"),
          };

          const formDataWithDetails = {
            firstName,
            lastName,
            email: formData.email,
            password: formData.password,
            timezone: getTimezoneString(),
            subdomain: subdomainResponse.subdomain,
          };

          const ghlContactId = await GhlService.createContact(formDataWithDetails, "", utm_Data);
          if (ghlContactId) {
            setGhlContactId(ghlContactId);
            await GhlService.addTags(ghlContactId, ["cc trial user", "Paid Trial"]);
          }

          const LsId = await LeadsquaredService.createtLead(
            formDataWithDetails,
            "",
            utm_Data
          );
          await LeadsquaredService.joinActivity({
            id: LsId,
            activityEvent: 201,
            activityValue: "Signup Attempt - Ad funnels",
          });
          const leadData = {
            leadId: LsId,
            fields: [
              { Attribute: "mx_Source_lp", Value: searchParams.get("Source_IP") },
            ],
          };
          await LeadsquaredService.updateLsField(leadData);
          setLeadSquaredId(LsId);
        } catch (error: any) {
          console.error("Error creating lead:", error);
        }

        setEmailSubmitted(true);
      }
    } catch (error: any) {
      console.error("Error processing email:", error);
      toast.error("Failed to process email. Please try again.");
    } finally {
      setIsCheckingEmail(false);
    }
  };

  useLayoutEffect(() => {
    if (cleanPlanId) {
      // const splitPlanId = planId?.split("-");
      // const attendeeNumber ;
      if (!allowedPlanIds.includes(cleanPlanId || "")) {
        navigate("/login", { replace: true });
      }
    }
  }, [cleanPlanId, planId]);

  return (
    <>
      {/* Auto-registration loading overlay */}
      {isAutoRegistering && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
            color: "white",
          }}
        >
          <CircularProgress size={60} sx={{ color: "white", mb: 2 }} />
          <Typography variant="h6" sx={{ color: "white", mb: 1 }}>
            Setting up your account...
          </Typography>
          <Typography variant="body2" sx={{ color: "white", opacity: 0.8 }}>
            Please wait while we complete your registration
          </Typography>
        </Box>
      )}
      {isProfessionalPlan ? (
        <Box
          sx={{
            display: showUpgradeModal ? "block" : "none",
          }}
        >
          <SalesExperienceYearlyOffer
            handleYearly={() => handleProceedWithSignup("All-In-One-USD-Yearly")}
            handleMonthly={() => handleProceedWithSignup("All-In-One-USD-Monthly")}
            isLoading={isLoadingUpgrade}
            trialPeriod={14}
          />
        </Box>
      ) : null}
      {!isProfessionalPlan ? (
        <Box
          sx={{
            display: showUpgradeModal ? "block" : "none",
          }}
        >
        <Modal
          open={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
          closeAfterTransition
          BackdropProps={{ timeout: 500 }}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{
              position: "relative",
              bgcolor: "white",
              borderRadius: "20px",
              boxShadow: 24,
              p: 0,
              maxWidth: "540px",
              width: { xs: "92%", sm: "90%" },
              maxHeight: "90vh",
              overflow: "auto",
              outline: "none",
            }}
          >
            <Box sx={{ p: { xs: 3, sm: 5 }, textAlign: "center" }}>
              <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                <Box
                  sx={{
                    bgcolor: "#F8B61C",
                    borderRadius: "999px",
                    px: 2,
                    py: 0.75,
                  }}
                >
                  <Typography
                    sx={{
                      color: "#803c0f",
                      fontSize: "12px",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.08em",
                      lineHeight: 1,
                    }}
                  >
                    SPECIAL ONE-TIME OFFER
                  </Typography>
                </Box>
              </Box>

              <Typography
                sx={{
                  color: "#1f2937",
                  fontSize: { xs: "28px", sm: "34px" },
                  fontWeight: 800,
                  lineHeight: 1.15,
                  mb: 1.5,
                }}
              >
                Wait! Upgrade to Annual & <br /> Save now
              </Typography>

              <Typography
                sx={{
                  color: "#5B6CFF",
                  fontSize: { xs: "16px", sm: "18px" },
                  fontWeight: 600,
                  mb: 3,
                }}
              >
                Plus exclusive bonuses
              </Typography>

              <Button
                variant="contained"
                fullWidth
                onClick={() => handleProceedWithSignup("Live-Webinars-USD-Yearly")}
                disabled={isLoadingUpgrade.accept || isLoadingUpgrade.decline}
                sx={{
                  background: "linear-gradient(90deg, #6F73F5 0%, #5E59D6 100%)",
                  color: "white",
                  py: 1.7,
                  fontSize: "16px",
                  fontWeight: 700,
                  borderRadius: "12px",
                  textTransform: "none",
                  mb: 2,
                  boxShadow: "none",
                  "&:hover": {
                    background: "linear-gradient(90deg, #6469EA 0%, #524ECB 100%)",
                    boxShadow: "none",
                  },
                }}
              >
                {isLoadingUpgrade.accept ? (
                  <CircularProgress size={24} sx={{ color: "white" }} />
                ) : (
                  "Upgrade to Annual Plan"
                )}
              </Button>

              <Box sx={{ textAlign: "center" }}>
                <Button
                  variant="text"
                  onClick={() => handleProceedWithSignup("Live-Webinars-USD-Monthly")}
                  disabled={isLoadingUpgrade.accept || isLoadingUpgrade.decline}
                  sx={{
                    color: "#6B7280",
                    fontSize: "14px",
                    textDecoration: "underline",
                    textTransform: "none",
                    "&:hover": {
                      backgroundColor: "transparent",
                      textDecoration: "underline",
                      color: "#4B5563",
                    },
                  }}
                >
                  {isLoadingUpgrade.decline ? (
                    <CircularProgress size={18} sx={{ color: "#6B7280" }} />
                  ) : (
                    "No thanks, Continue with monthly"
                  )}
                </Button>
              </Box>
            </Box>
          </Box>
        </Modal>
        </Box>
      ) : null}
      <Box
        sx={{
          display: showUpgradeModal && isProfessionalPlan ? "none" : "flex",
          flexDirection: "column",
          minHeight: "100vh",
          width: "100vw",
          background: "linear-gradient(135deg, #0a0a0f 0%, #111827 40%, #1e3a5f 100%)",
          backgroundAttachment: "fixed",
          color: "#1f2937",
          fontFamily: "Inter",
        }}
      >
        {/* Fixed Header */}
        <Box
          sx={{
            position: "sticky",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 99,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: { xs: "16px 24px", sm: "24px 32px" },
            bgcolor: { xs: "#000000", sm: "transparent" },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <img
              src={LogoDarkMode}
              alt="logo"
              style={{ height: 24, cursor: "pointer", maxWidth: "100%" }}
              onClick={() => window.open("https://easywebinar.com/", "_blank")}
            />
          </Box>
          <Button
            variant="contained"
            endIcon={<ArrowForward />}
            size="small"
            onClick={() => navigate("/login")}
            sx={{
              bgcolor: "#6066E9",
              color: "#fff",
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 600,
              "&:hover": { bgcolor: "#4B4EB5" },
            }}
          >
            Login
          </Button>
        </Box>

        {/* Main content wrapper */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
            p: { xs: "20px 20px 30px", sm: "20px 40px 40px" },
          }}
        >
          {/* Single centered box container */}
          <Box
            sx={{
              width: "100%",
              maxWidth: "480px",
              bgcolor: "#ffffff",
              borderRadius: "16px",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.15)",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Main content area */}
            {isLoadingPlan ? (
              <SignupFormSkeleton />
            ) : (
              <Box sx={{ p: { xs: 2, sm: 3 } }}>
                {/* Header Section */}
                <Box sx={{ textAlign: "center", mb: 3 }}>
                  {/* Start today badge */}
                  <Box
                    sx={{
                      display: "inline-block",
                      bgcolor: "#ECECEC80",
                      color: "#000",
                      fontSize: "12px",
                      fontWeight: 600,
                      px: 2,
                      py: 1,
                      borderRadius: "4px",
                      mb: 1.85,
                    }}
                  >
                    Start today
                  </Box>

                  {/* Professional Plan Title */}
                  <Typography
                    sx={{
                      color: "#1f2937",
                      fontSize: { xs: "24px", sm: "28px" },
                      fontWeight: 700,
                      mb: 1,
                    }}
                  >
                    {isProfessionalPlan ? "Professional Plan" : "Launch Plan"}
                  </Typography>

                  {/* Attendees info */}
                  <Typography
                    sx={{
                      color: "#6b7280",
                      fontSize: "14px",
                    }}
                  >
                    {parseInt(planId?.match(/-(\d+)$/)?.[1] || "200")} Attendees • Full Platform Access
                  </Typography>
                </Box>

                {/* Green Trial Access Box */}
                {/* Trial Access Card with Green Header */}
                <Box
                  sx={{
                    bgcolor: "#F6F6F6",
                    borderRadius: "8px",
                    overflow: "hidden",
                    mb: 2,
                    border: "1px solid #e5e7eb",
                  }}
                >
                  {/* Green Header */}
                  <Box
                    sx={{
                      bgcolor: "#00BC7D",
                      p: "4px 16px",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <path d="M5 13l4 4L19 7" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      <Typography sx={{ color: "white", fontWeight: 600, fontSize: "14px" }}>
                        14-Day full access
                      </Typography>
                    </Box>
                    <Typography sx={{ color: "white", fontWeight: 700, fontSize: "20px" }}>
                      $1.00
                    </Typography>
                  </Box>

                  {/* Content inside gray box */}
                  <Box sx={{ p: { xs: "10px 12px", sm: "14px 16px" } }}>
                    {/* Feature Checkmarks Row */}
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                      {["All Features", `${parseInt(planId?.match(/-(\d+)$/)?.[1] || "200")} Attendees`, "Cancel Anytime"].map((feature, idx) => (
                        <Box key={idx} sx={{ display: "flex", alignItems: "center", gap: 0.5, backgroundColor: "#fff", borderRadius: "20px", padding: "4px 8px" }}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                            <path d="M5 13l4 4L19 7" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                          <Typography sx={{ color: "#374151", fontSize: "12px", fontWeight: 500 }}>{feature}</Typography>
                        </Box>
                      ))}
                    </Box>

                    {/* Price after trial info */}
                    <Box>
                      <Typography sx={{ color: "#1f2937", fontSize: "14px", fontWeight: 500 }}>
                        Then <strong>${calculateDynamicPricing().monthlyPrice?.replace('$', '')}/month</strong> after 14 days
                      </Typography>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.3 }}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                          <circle cx="12" cy="12" r="10" stroke="#9CA3AF" strokeWidth="2" />
                          <path d="M12 6v6l4 2" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                        <Typography sx={{ color: "#9CA3AF", fontSize: "11px" }}>Reminder on Day 13</Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>


                {/* FREE With Your Trial Section */}
                <Box
                  sx={{
                    backgroundColor: "#F6F6F6",
                    borderRadius: "8px",
                    overflow: "hidden",
                    mb: 2,
                  }}
                >
                  {/* Header */}
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: "8px 16px", backgroundColor: "#EBE5E5" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                        <path d="M9 12l2 2 4-4" stroke="#1f2937" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                        <circle cx="12" cy="12" r="10" stroke="#1f2937" strokeWidth="2" />
                      </svg>
                      <Typography sx={{ color: "#1f2937", fontWeight: 700, fontSize: "15px" }}>
                        FREE With Your Trial
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        bgcolor: "#374151",
                        color: "white",
                        fontSize: "12px",
                        fontWeight: 600,
                        px: 2,
                        py: 0.5,
                        borderRadius: "6px",
                      }}
                    >
                      Included
                    </Box>
                  </Box>

                  <Box sx={{ p: "14px 18px" }}>
                    {/* Bonus items grid */}
                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mb: 2 }}>
                      {/* 7-Figure Webinar Script */}
                      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0, marginTop: 2 }}>
                          <path d="M2 7.33333L14 4V12L2 9.33333V7.33333Z" stroke="#4A5565" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M7.7338 11.1995C7.66377 11.4534 7.54441 11.691 7.38254 11.8987C7.22067 12.1065 7.01947 12.2803 6.79042 12.4103C6.56137 12.5403 6.30895 12.624 6.04759 12.6564C5.78622 12.6889 5.52103 12.6695 5.26714 12.5995C5.01325 12.5294 4.77564 12.4101 4.56788 12.2482C4.36012 12.0863 4.18628 11.8851 4.05627 11.6561C3.92627 11.427 3.84266 11.1746 3.8102 10.9133C3.77775 10.6519 3.7971 10.3867 3.86714 10.1328" stroke="#4A5565" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>

                        <Typography sx={{ color: "#364153", fontSize: "13px", lineHeight: 1.4 }}>7-Figure Webinar Script</Typography>
                      </Box>

                      {/* How To Launch, Test & Scale Your Webinar */}
                      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0, marginTop: 2 }}>
                          <path d="M3.00033 11.0002C2.00033 11.8402 1.66699 14.3335 1.66699 14.3335C1.66699 14.3335 4.16033 14.0002 5.00033 13.0002C5.47366 12.4402 5.46699 11.5802 4.94033 11.0602C4.6812 10.8129 4.33985 10.67 3.98181 10.6589C3.62376 10.6478 3.27424 10.7694 3.00033 11.0002Z" stroke="#4A5565" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M8 10.0008L6 8.00082C6.35476 7.08044 6.80147 6.19819 7.33333 5.36748C8.11012 4.12547 9.19175 3.10285 10.4753 2.39688C11.7589 1.69091 13.2018 1.32506 14.6667 1.33415C14.6667 3.14748 14.1467 6.33415 10.6667 8.66748C9.82459 9.19996 8.93123 9.64664 8 10.0008Z" stroke="#4A5565" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M6.00033 8.00034H2.66699C2.66699 8.00034 3.03366 5.98034 4.00033 5.33367C5.08033 4.61367 7.33366 5.33367 7.33366 5.33367" stroke="#4A5565" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M8 9.99935V13.3327C8 13.3327 10.02 12.966 10.6667 11.9993C11.3867 10.9193 10.6667 8.66602 10.6667 8.66602" stroke="#4A5565" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <Typography sx={{ color: "#364153", fontSize: "13px", lineHeight: 1.4 }}>How To Launch, Test & Scale Your Webinar</Typography>
                      </Box>

                      {/* 7-Figure Webinar Deck Template */}
                      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0, marginTop: 2 }}>
                          <path d="M13.5 1H2.5C2.10218 1 1.72064 1.15804 1.43934 1.43934C1.15804 1.72064 1 2.10218 1 2.5V5C1 5.39782 1.15804 5.77936 1.43934 6.06066C1.72064 6.34196 2.10218 6.5 2.5 6.5H13.5C13.8978 6.5 14.2794 6.34196 14.5607 6.06066C14.842 5.77936 15 5.39782 15 5V2.5C15 2.10218 14.842 1.72064 14.5607 1.43934C14.2794 1.15804 13.8978 1 13.5 1ZM14 5C14 5.13261 13.9473 5.25979 13.8536 5.35355C13.7598 5.44732 13.6326 5.5 13.5 5.5H2.5C2.36739 5.5 2.24021 5.44732 2.14645 5.35355C2.05268 5.25979 2 5.13261 2 5V2.5C2 2.36739 2.05268 2.24021 2.14645 2.14645C2.24021 2.05268 2.36739 2 2.5 2H13.5C13.6326 2 13.7598 2.05268 13.8536 2.14645C13.9473 2.24021 14 2.36739 14 2.5V5Z" fill="#171938" />
                          <path d="M6.5 8H2.5C2.10218 8 1.72064 8.15804 1.43934 8.43934C1.15804 8.72064 1 9.10218 1 9.5V13.5C1 13.8978 1.15804 14.2794 1.43934 14.5607C1.72064 14.842 2.10218 15 2.5 15H6.5C6.89782 15 7.27936 14.842 7.56066 14.5607C7.84196 14.2794 8 13.8978 8 13.5V9.5C8 9.10218 7.84196 8.72064 7.56066 8.43934C7.27936 8.15804 6.89782 8 6.5 8ZM7 13.5C7 13.6326 6.94732 13.7598 6.85355 13.8536C6.75979 13.9473 6.63261 14 6.5 14H2.5C2.36739 14 2.24021 13.9473 2.14645 13.8536C2.05268 13.7598 2 13.6326 2 13.5V9.5C2 9.36739 2.05268 9.24021 2.14645 9.14645C2.24021 9.05268 2.36739 9 2.5 9H6.5C6.63261 9 6.75979 9.05268 6.85355 9.14645C6.94732 9.24021 7 9.36739 7 9.5V13.5Z" fill="#171938" />
                          <path d="M10 9.5H14.5C14.6326 9.5 14.7598 9.44732 14.8536 9.35355C14.9473 9.25979 15 9.13261 15 9C15 8.86739 14.9473 8.74021 14.8536 8.64645C14.7598 8.55268 14.6326 8.5 14.5 8.5H10C9.86739 8.5 9.74021 8.55268 9.64645 8.64645C9.55268 8.74021 9.5 8.86739 9.5 9C9.5 9.13261 9.55268 9.25979 9.64645 9.35355C9.74021 9.44732 9.86739 9.5 10 9.5Z" fill="#4C46C8" />
                          <path d="M14.5 11H10C9.86739 11 9.74021 11.0527 9.64645 11.1464C9.55268 11.2402 9.5 11.3674 9.5 11.5C9.5 11.6326 9.55268 11.7598 9.64645 11.8536C9.74021 11.9473 9.86739 12 10 12H14.5C14.6326 12 14.7598 11.9473 14.8536 11.8536C14.9473 11.7598 15 11.6326 15 11.5C15 11.3674 14.9473 11.2402 14.8536 11.1464C14.7598 11.0527 14.6326 11 14.5 11Z" fill="#4C46C8" />
                          <path d="M14.5 13.5H10C9.86739 13.5 9.74021 13.5527 9.64645 13.6464C9.55268 13.7402 9.5 13.8674 9.5 14C9.5 14.1326 9.55268 14.2598 9.64645 14.3536C9.74021 14.4473 9.86739 14.5 10 14.5H14.5C14.6326 14.5 14.7598 14.4473 14.8536 14.3536C14.9473 14.2598 15 14.1326 15 14C15 13.8674 14.9473 13.7402 14.8536 13.6464C14.7598 13.5527 14.6326 13.5 14.5 13.5Z" fill="#4C46C8" />
                        </svg>
                        <Typography sx={{ color: "#364153", fontSize: "13px", lineHeight: 1.4 }}>7-Figure Webinar Deck Template</Typography>
                      </Box>

                      {/* How To Position And Craft Your Irresistible Offer */}
                      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0, marginTop: 2 }}>
                          <path d="M10.0003 1.33398H4.00033C3.6467 1.33398 3.30756 1.47446 3.05752 1.72451C2.80747 1.97456 2.66699 2.3137 2.66699 2.66732V13.334C2.66699 13.6876 2.80747 14.0267 3.05752 14.2768C3.30756 14.5268 3.6467 14.6673 4.00033 14.6673H12.0003C12.3539 14.6673 12.6931 14.5268 12.9431 14.2768C13.1932 14.0267 13.3337 13.6876 13.3337 13.334V4.66732L10.0003 1.33398Z" stroke="#4A5565" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M9.33301 1.33398V4.00065C9.33301 4.35427 9.47348 4.69341 9.72353 4.94346C9.97358 5.19351 10.3127 5.33398 10.6663 5.33398H13.333" stroke="#4A5565" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M6.66634 6H5.33301" stroke="#4A5565" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M10.6663 8.66602H5.33301" stroke="#4A5565" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M10.6663 11.334H5.33301" stroke="#4A5565" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <Typography sx={{ color: "#364153", fontSize: "13px", lineHeight: 1.4 }}>How To Position And Craft Your Irresistible Offer</Typography>
                      </Box>
                    </Box>

                    {/* Checklist item - centered */}
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="12" r="10" stroke="#000" strokeWidth="2" />
                        <path d="M9 12l2 2 4-4" stroke="#000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      <Typography sx={{ color: "#364153", fontSize: "13px" }}>Webinar Funnel Launch Checklist</Typography>
                    </Box>
                  </Box>
                </Box>

                {/* One-Time Offer Section */}
                {isEbookFlow && (
                  <Box
                    onClick={() => setIncludeEbook(!includeEbook)}
                    sx={{
                      border: "2px dashed #F48F2A",
                      borderRadius: "16px",
                      overflow: "hidden",
                      mb: 2,
                      cursor: "pointer",
                      transition: "all 0.2s",
                      bgcolor: "#FFF8E1",
                    }}
                  >
                    {/* Orange header */}
                    <Box
                      sx={{
                        bgcolor: "#F48F2A",
                        color: "white",
                        px: 2.5,
                        py: 1.2,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                          <rect x="3" y="8" width="18" height="13" rx="2" stroke="white" strokeWidth="2" />
                          <path d="M12 8V6a2 2 0 012-2h0a2 2 0 012 2v2" stroke="white" strokeWidth="2" />
                          <path d="M12 8V6a2 2 0 00-2-2h0a2 2 0 00-2 2v2" stroke="white" strokeWidth="2" />
                        </svg>
                        <Typography sx={{ fontWeight: 700, fontSize: "14px", letterSpacing: "0.5px" }}>
                          ONE-TIME OFFER
                        </Typography>
                      </Box>
                      <Typography sx={{ fontWeight: 700, fontSize: "22px" }}>+${bookPrice}</Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, p: "4px 13px", bgcolor: "#FFFFBB" }}>
                      <svg width="20" height="15" viewBox="0 0 20 15" fill="none" style={{ flexShrink: 0 }}>
                        <path d="M11.4839 14.7407C11.1466 14.7407 10.8239 14.638 10.5159 14.4327C10.2079 14.242 9.95126 13.9927 9.74593 13.6847C9.55526 13.3767 9.45993 13.0687 9.45993 12.7607C9.45993 12.4087 9.6066 12.0347 9.89993 11.6387C10.1933 11.2427 10.5379 10.854 10.9339 10.4727C11.3446 10.0767 11.7039 9.73937 12.0119 9.4607H2.92593C2.08993 9.4607 1.49593 9.3947 1.14393 9.2627C0.79193 9.1307 0.513263 8.9107 0.30793 8.6027C0.102596 8.30937 -7.02515e-05 7.8987 -7.02515e-05 7.3707C-7.02515e-05 6.8427 0.0952631 6.43204 0.28593 6.1387C0.491263 5.84537 0.76993 5.62537 1.12193 5.4787C1.47393 5.3467 2.07526 5.2807 2.92593 5.2807H12.0119L10.9339 4.2467C10.3326 3.66004 9.9366 3.2127 9.74593 2.9047C9.55526 2.58204 9.45993 2.27404 9.45993 1.9807C9.45993 1.6727 9.55526 1.3647 9.74593 1.0567C9.9366 0.748702 10.1859 0.499369 10.4939 0.308702C10.8166 0.103369 11.1466 0.000702024 11.4839 0.000702024C11.7773 0.000702024 12.0926 0.0960354 12.4299 0.286702C12.7673 0.462703 13.1633 0.770703 13.6179 1.2107L19.9539 7.3707L13.7499 13.3987C13.2219 13.8974 12.7893 14.242 12.4519 14.4327C12.1146 14.638 11.7919 14.7407 11.4839 14.7407Z" fill="#D60303" />
                      </svg>
                      <Checkbox
                        checked={includeEbook}
                        onChange={(e) => {
                          e.stopPropagation();
                          setIncludeEbook(e.target.checked);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                          p: 0,
                          color: "#000",
                          "& .MuiSvgIcon-root": { fontSize: 24 },
                          "&.Mui-checked": { color: "#000" },
                        }}
                      />
                      <Typography sx={{ color: "#000", fontSize: "14px", }}>
                        Add the sales experience book to your checkout
                      </Typography>
                    </Box>
                    {/* Content */}
                    <Box sx={{ p: 2.5, bgcolor: "#FEFCE8" }}>
                      {/* Checkbox row with arrow */}
                      <Typography sx={{ color: "#6b7280", fontSize: "14px", mb: 2.5, lineHeight: 1.6 }}>
                        20+ proven formulas & 6-steps framework for launching and scaling your webinar funnel.
                      </Typography>

                      {/* Feature checkmarks - 2x2 grid */}
                      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "0.6fr 1fr", sm: "0.8fr 1fr" }, gap: 1.5, mb: 2.5 }}>
                        {[
                          "6-Step Framework",
                          "Proven Strategies from 196,000 successful webinars.",
                          "4X Revenue Blueprint",
                          "Sprint Method"
                        ].map((item, idx) => (
                          <Box key={idx} sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                            <Box
                              sx={{
                                width: 18,
                                height: 18,
                                borderRadius: "4px",
                                bgcolor: "#A09797",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                                mt: 0.2,
                              }}
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                <path d="M5 13l4 4L19 7" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </Box>
                            <Typography sx={{ color: "#4b5563", fontSize: "12px", lineHeight: 1.4, mt: 0.3 }}>{item}</Typography>
                          </Box>
                        ))}
                      </Box>

                      {/* Add button */}
                      {!includeEbook && <Button
                        fullWidth
                        variant="contained"
                        onClick={(e) => {
                          e.stopPropagation();
                          setIncludeEbook(true);
                        }}
                        sx={{
                          bgcolor: "#F48F2A",
                          color: "#ffffff !important",
                          borderRadius: "10px",
                          py: 1.5,
                          textTransform: "none",
                          fontWeight: 600,
                          fontSize: "15px",
                          boxShadow: "none",
                          "&:hover": { bgcolor: "#F57F17", boxShadow: "none" },
                        }}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ marginRight: 8 }}>
                          <path d="M5 13l4 4L19 7" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        Add this book for ${bookPrice}
                      </Button>}
                    </Box>
                  </Box>
                )}

                {/* Total Due Today Section */}
                <Box
                  sx={{
                    bgcolor: "#1f2937",
                    borderRadius: "12px",
                    p: { xs: "12px 14px", sm: 2.5 },
                    mb: 3,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Box>
                    <Typography sx={{ color: "white", fontWeight: 700, fontSize: "18px" }}>
                      Total due today
                    </Typography>
                    <Typography sx={{ color: "#9CA3AF", fontSize: "12px" }}>
                      Trial only
                    </Typography>
                  </Box>
                  <Typography sx={{ color: "white", fontWeight: 700, fontSize: "32px" }}>
                    ${isEbookFlow ? totalDueToday : 1}
                  </Typography>
                </Box>

                {/* Email Input Section - shown when email not yet submitted */}
                {!emailSubmitted && (
                  <Box sx={{ mb: 3 }}>
                    <Typography sx={{ color: "#1f2937", fontWeight: 600, fontSize: "14px", mb: 1 }}>
                      Enter your email to get started
                    </Typography>
                    <TextField
                      variant="outlined"
                      placeholder="you@company.com"
                      value={formData.email}
                      onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                      fullWidth
                      disabled={isCheckingEmail}
                      onKeyPress={(e) => {
                        if (e.key === "Enter") {
                          handleEmailSubmit();
                        }
                      }}
                      sx={{
                        mb: 2,
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "8px",
                          bgcolor: "#f9fafb",
                        },
                        "& .MuiOutlinedInput-input": {
                          height: "0.8em",
                        },
                      }}
                    />
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={handleEmailSubmit}
                      disabled={isCheckingEmail || !formData.email.trim()}
                      sx={{
                        bgcolor: "#0382F7",
                        color: "#fff",
                        borderRadius: "8px",
                        textTransform: "none",
                        fontWeight: 600,
                        py: 1.5,
                        "&:hover": { bgcolor: "#006dd1" },
                        "&.Mui-disabled": {
                          bgcolor: "#cccccc",
                          color: "#666666",
                        },
                      }}
                    >
                      {isCheckingEmail ? (
                        <CircularProgress size={24} sx={{ color: "white" }} />
                      ) : (
                        "Continue"
                      )}
                    </Button>
                  </Box>
                )}

                {/* Account exists message - shown after email submitted */}
                {emailSubmitted && accountExists && (
                  <Box
                    sx={{
                      width: "100%",
                      p: 2,
                      mb: 2,
                      borderRadius: "8px",
                      bgcolor: "#F3F6FF",
                      border: "1px solid #E5ECFF",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        color: "#1f2937",
                        fontSize: "14px",
                        fontWeight: 500,
                        textAlign: "center",
                        lineHeight: 1.6,
                      }}
                    >
                      Already using EasyWebinar?{" "}
                      <Box
                        component={Link}
                        to="/login?page=subscription"
                        sx={{
                          color: "#6066E9",
                          fontWeight: 600,
                          textDecoration: "none",
                          "&:hover": { textDecoration: "underline" },
                        }}
                      >
                        Click here
                      </Box>{" "}
                      to buy just the book
                    </Typography>
                  </Box>
                )}

                {/* Card Details Section - shown after email submitted */}
                {emailSubmitted && !accountExists && !isTrial && (
                  <Box sx={{ mb: 3 }}>
                    <Typography sx={{ color: "#1f2937", fontWeight: 600, fontSize: "14px", mb: 1.5 }}>
                      Card Details
                    </Typography>

                    {/* Chargebee Card Component */}
                    <Box
                      sx={{
                        position: 'relative',
                        mb: 2,
                        "& .CbHosted": {
                          borderRadius: "8px",
                          border: "1px solid #d1d5db",
                          p: { xs: "10px 12px", sm: "14px 16px" },
                          bgcolor: "#f9fafb",
                          "&:hover, &--focus": {
                            borderColor: "#9CA3AF",
                          },
                        },
                      }}
                    >
                      <CardComponent
                        ref={cardRef}
                        currency="USD"
                        showTestCards={true}
                        styles={{
                          base: {
                            fontSize: "16px",
                            fontFamily: "Inter, sans-serif",
                            color: "#1f2937",
                            "::placeholder": {
                              color: "#9CA3AF",
                            },
                          },
                          invalid: {
                            color: "#ef4444",
                          },
                        }}
                        onChange={(status: any) => {
                          if (status.error) {
                            setCardError(status.error.message);
                            setIsCardValid(false);
                          } else if (!status.complete) {
                            setCardError(null);
                            setIsCardValid(false);
                          } else {
                            setCardError(null);
                            setIsCardValid(true);
                          }
                        }}
                        onReady={() => {
                          setIsCardReady(true);
                          setIsCardValid(false);
                        }}
                      />
                    </Box>
                    {cardError && (
                      <Typography color="error" fontSize="12px" mb={2}>
                        {cardError}
                      </Typography>
                    )}
                  </Box>
                )}

                {/* Your custom URL - shown after email submitted */}
                {emailSubmitted && !accountExists && (
                  <Box sx={{ mb: 3 }}>
                    <Typography sx={{ color: "#6b7280", fontSize: "13px", mb: 1 }}>
                      Your custom url
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <TextField
                        value={formData.subdomain || ""}
                        onChange={(e) => {
                          setFormData((prev) => ({
                            ...prev,
                            subdomain: e.target.value.toLowerCase(),
                          }));
                          setFormErrors((prev) => ({
                            ...prev,
                            subdomain: "",
                          }));
                        }}
                        error={!!formErrors.subdomain}
                        helperText={formErrors.subdomain}
                        placeholder="yourname"
                        sx={{
                          flex: 1,
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "8px 0 0 8px",
                            bgcolor: "#f9fafb",
                          },
                          "& .MuiOutlinedInput-input": {
                            height: "1em",
                          },
                        }}
                      />
                      <Box
                        sx={{
                          bgcolor: "#f3f4f6",
                          border: "1px solid #d1d5db",
                          borderLeft: "none",
                          borderRadius: "0 8px 8px 0",
                          px: 2,
                          py: 1.85,
                          color: "#6b7280",
                          fontSize: "14px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        .easywebinar.live
                      </Box>
                    </Box>
                    {domainSuggestions.length > 0 && (
                      <Box mt={1}>
                        <Typography color="primary" fontSize="12px" mb={0.5}>
                          Available alternatives:
                        </Typography>
                        <Box display="flex" gap={0.5} flexWrap="wrap">
                          {domainSuggestions.map((suggestion, index) => (
                            <Button
                              key={index}
                              variant="outlined"
                              size="small"
                              onClick={() => {
                                setFormData((prev) => ({
                                  ...prev,
                                  subdomain: suggestion,
                                }));
                                setFormErrors((prev) => ({
                                  ...prev,
                                  subdomain: "",
                                }));
                                setDomainSuggestions([]);
                              }}
                              sx={{
                                textTransform: "none",
                                fontSize: "11px",
                                py: 0.3,
                                px: 1,
                              }}
                            >
                              {suggestion}
                            </Button>
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Box>
                )}

                {/* Trust badges - shown after email submitted */}
                {emailSubmitted && !accountExists && (
                  <Box sx={{ display: "flex", justifyContent: "center", gap: 3, mb: 3 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                        <rect x="3" y="11" width="18" height="11" rx="2" stroke="#6b7280" strokeWidth="2" />
                        <path d="M7 11V7a5 5 0 0110 0v4" stroke="#6b7280" strokeWidth="2" />
                      </svg>
                      <Typography sx={{ color: "#6b7280", fontSize: "12px" }}>Secure Checkout</Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="12" r="10" stroke="#6b7280" strokeWidth="2" />
                        <path d="M9 12l2 2 4-4" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      <Typography sx={{ color: "#6b7280", fontSize: "12px" }}>Cancel Anytime</Typography>
                    </Box>
                  </Box>
                )}

                {/* Submit Button - shown after email submitted */}
                {emailSubmitted && !accountExists && (
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleGetStarted}
                    disabled={isLoading || isAutoRegistering || (!isTrial && !isCardValid)}
                    sx={{
                      bgcolor: "#10B981",
                      color: "white",
                      borderRadius: "8px",
                      py: 1.5,
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: "16px",
                      mb: 2,
                      "&:hover": { bgcolor: "#059669" },
                      "&.Mui-disabled": {
                        bgcolor: "#cccccc",
                        color: "#666666",
                      },
                    }}
                  >
                    {isLoading || isAutoRegistering ? (
                      <CircularProgress size={24} sx={{ color: "white" }} />
                    ) : (
                      <>
                        Start my ${isEbookFlow ? totalDueToday : 1} trial now
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ marginLeft: 8 }}>
                          <path d="M5 12h14M12 5l7 7-7 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </>
                    )}
                  </Button>
                )}

                {/* SSL Badge - shown after email submitted */}
                {emailSubmitted && !accountExists && (
                  <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 0.5 }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                      <rect x="3" y="11" width="18" height="11" rx="2" stroke="#9CA3AF" strokeWidth="2" />
                      <path d="M7 11V7a5 5 0 0110 0v4" stroke="#9CA3AF" strokeWidth="2" />
                    </svg>
                    <Typography sx={{ color: "#9CA3AF", fontSize: "11px" }}>SSL Encrypted Payment</Typography>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* <Modal
        open={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{ timeout: 500 }}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            position: "relative",
            bgcolor: "white",
            borderRadius: "20px",
            boxShadow: 24,
            p: 0,
            maxWidth: "540px",
            width: { xs: "92%", sm: "90%" },
            maxHeight: "90vh",
            overflow: "auto",
            outline: "none",
          }}
        >
          <Box sx={{ p: { xs: 3, sm: 5 }, textAlign: "center" }}>
            <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
              <Box
                sx={{
                  bgcolor: "#F8B61C",
                  borderRadius: "999px",
                  px: 2,
                  py: 0.75,
                }}
              >
                <Typography
                  sx={{
                    color: "#803c0f",
                    fontSize: "12px",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    lineHeight: 1,
                  }}
                >
                  SPECIAL ONE-TIME OFFER
                </Typography>
              </Box>
            </Box>

            <Typography
              sx={{
                color: "#1f2937",
                fontSize: { xs: "28px", sm: "34px" },
                fontWeight: 800,
                lineHeight: 1.15,
                mb: 1.5,
              }}
            >
              Wait! Upgrade to Annual & <br /> Save $696
            </Typography>

            <Typography
              sx={{
                color: "#5B6CFF",
                fontSize: { xs: "16px", sm: "18px" },
                fontWeight: 600,
                mb: 3,
              }}
            >
              Plus $2,985 in Exclusive Bonuses
            </Typography>

            <Button
              variant="contained"
              fullWidth
              onClick={() => handleProceedWithSignup("All-In-One-USD-Yearly")}
              disabled={isLoadingUpgrade.accept || isLoadingUpgrade.decline}
              sx={{
                background: "linear-gradient(90deg, #6F73F5 0%, #5E59D6 100%)",
                color: "white",
                py: 1.7,
                fontSize: "16px",
                fontWeight: 700,
                borderRadius: "12px",
                textTransform: "none",
                mb: 2,
                boxShadow: "none",
                "&:hover": {
                  background: "linear-gradient(90deg, #6469EA 0%, #524ECB 100%)",
                  boxShadow: "none",
                },
              }}
            >
              {isLoadingUpgrade.accept ? (
                <CircularProgress size={24} sx={{ color: "white" }} />
              ) : (
                "Upgrade to Annual Plan"
              )}
            </Button>

            <Box sx={{ textAlign: "center" }}>
              <Button
                variant="text"
                onClick={() => handleProceedWithSignup("All-In-One-USD-Monthly")}
                disabled={isLoadingUpgrade.accept || isLoadingUpgrade.decline}
                sx={{
                  color: "#6B7280",
                  fontSize: "14px",
                  textDecoration: "underline",
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: "transparent",
                    textDecoration: "underline",
                    color: "#4B5563",
                  },
                }}
              >
                {isLoadingUpgrade.decline ? (
                  <CircularProgress size={18} sx={{ color: "#6B7280" }} />
                ) : (
                  "No thanks, Continue with monthly"
                )}
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal> */}

      <AccountVerification
        onClose={handleVerificationDialogClose}
        open={verificationDialogOpen}
        onProceed={handleVerificationDialogProceed}
        phoneNumber=""
        email={formData.email}
        password={formData.password}
        companyId={verificationData.companyId}
        companyName={verificationData.companyName}
        infLeadSquaredId={verificationData.infLeadSquaredId}
      />
    </>
  );
};
export default SignupSales14DayTrialWithBook;
